/**
 * BuildingInteriorModule — walkable building interiors, the FOUNDATION
 * (2026-08-13). The ship-deck doctrine on the ground:
 *
 *   · Eligible buildings (living quarters, civic halls, guild-scale — the
 *     enterables registry, fed by the settlement builder and the deed
 *     renderer) grow a door prompt: E at the door steps inside.
 *   · Entering HIDES the building's exterior (its instances + collider) and
 *     builds a SAME-SCENE procedural shell inside the real footprint at the
 *     real position: floors every 3.4 m up the building's true height,
 *     stair runs between them, windowed perimeter walls — the glass panes
 *     are true openings, so the view out IS the street you walked in from.
 *   · The KCC walks it all on plain box colliders (structure-floor
 *     grounding is existing physics law); movement stays the normal
 *     server-validated ground path — no world hop, no teleport.
 *   · E at the door (or straying far from the building) steps out and
 *     restores the exterior exactly.
 *
 * Interior surfaces are MeshBasicMaterial (the baked-materials law: interior
 * readability must never depend on scene lighting).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { disposeLater } from '../lib/gpu_dispose.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyIs } from '../lib/keybinds.js';
import { Phase, makeSystem } from '../engine-compat.js';
import {
  enterables, doorWorld, interiorSpec, clampIndoor, FLOOR_H, type EnterableBuilding, type InteriorSpec,
} from '../lib/building_interiors.js';
import { canEnterStructure, structureDisplayName, type PermLevel } from '../lib/structure_access.js';
import { interactFocus } from '../lib/interact_focus.js';
import { tableStamp } from '../lib/fast_find.js'; // (slice H) the furniture catalog's memo key
import { FURNITURE, type FurnitureSpec } from '../lib/furniture_catalog.js';
import { furnitureFit } from '../lib/furniture_place.js';
import { RAIN_SHELTER_LAYER } from '../lib/rain_shelter.js';
import { resolvePurpose, purposeSuffix, type InteriorPurpose, type PurposeInfo } from '../lib/interior_purpose.js';
import { purposePlan } from '../lib/interior_plans.js';
import { packPlan, furnishPack, type FurnishPack } from '../lib/furnish_packs.js';
import { shellMaterials, signTexture, furnitureMaterial, type InteriorLook } from './interior/interior_look.js';
import type { ShellMaterials } from './interior/interior_look.js'; // (its own line: slice D's gate pins the line above)
import { resolveLook } from './interior/interior_look.js'; // (slice H — its own line too: the prewarm reads a door's tradition before the visit exists)
import {
  interiorPlannerEnabled, legacyFurnisherFor, furnishRouteFor, layoutFor, isPackInterior, planInputFor, seatView, residentRoomOf, signSeat,
  pieceColliderBox, setActiveInterior, planCacheStats, LAYOUT_PIN_FLAG, DIGEST_RE, propColliderBox, fixtureOptionsFor, fixturesShown,
  pinnedTraditionOrder, pinHasDigest, itemColliderBox, itemColliderPicks, interiorSafetyEnabled, ownsLastResortGround, safetyGroundBox, rescueVerdict,
  SAFETY_REACH_M, RESCUE_UNDER_M, fixtureModelsEnabled, fixtureModelPose,
  partitionBoxesFor, presenceReportable, visitRoomAt, PRESENCE_DEBOUNCE_MS, PRESENCE_RETRY_MS, // (slice G) the partitions and who is where
  interiorMergeEnabled, interiorPrewarmEnabled, interiorStreamEnabled, seatOrder, prefetchOrder, storeyAt, PREWARM_DWELL_MS, PREWARM_KEEP_MS, NEAR_STOREYS, // (slice H) the door's budget
  type ActiveInterior, type LayoutPin, type LegacyFurnisher, type SeatPiece, type SeatView, type DetailLevel, type WiringTradition,
} from '../lib/interior_wiring.js';
import { structureSeats } from '../lib/structure_seat.js';
import { planRoom, planClientStats } from './interior/interior_plan_client.js';
import { buildShellBatches } from './interior/interior_shell_batch.js'; // (slice H) the shell as a handful of draws
import { buildInteriorDebug, interiorDebugAllowed, type InteriorDebugHandle } from './interior/interior_debug.js';
import { clampDetail, styleFor } from '../lib/interior_style.js';
import { bodyLightOf } from '../lib/interior_style.js'; // (slice H) the room's light on a body — its own line (gates pin the one above)
import { setIndoorBodyLight } from '../lib/indoor_body.js';
import { kitProp, isKitProp, shellFixtures, PROP_ROLE, type KitPropSpec, type PropRole } from '../lib/interior_kit_props.js';
import { fixtureModelSeat } from '../lib/interior_kits.js';
import { mergeStaticMeshes } from '../lib/static_merge.js';
// (slice G task 2 — their own lines) THE TENANT'S DOOR: the let store, the two levers, the pure door rules
import { letUnitsOf, letStoreVersion, unitNumber as letUnitNumber } from '../lib/rental_client.js';
import { RENTALS_FLAG } from '../lib/rentals.js';
import { ROOMS_FLAG } from '../lib/interior_rooms.js';
import { unitDoorsOf, doorShut, doorTenant, doorRelation, doorPassNext, doorBlocks, doorBox, leafBoxes, type UnitDoor } from '../lib/interior_doors.js';
import { activePreset } from '../lib/quality_tiers.js';
import type { InteriorDescriptor, PlanReport } from '../lib/interior_descriptor.js';
import type { ResidentRoom } from '../lib/home_layout.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AssetModule } from './AssetModule.js';

const DOOR_RANGE_M = 4.5;
const AUTO_EXIT_M = 26;
/** A presence report the TENANT'S DOOR refused is tried again after this long (ms) — the lease may have ended. */
const DOOR_RETRY_MS = 10_000;
/** The sign inside the door (THE PURPOSE LAW): right of the doorway. Its seat is `interior_wiring.signSeat` — on the
 *  window HEADER band since 2026-10-01 (it hung eye-high at 2.25 m, inside the window band: across a pane in most
 *  rooms); the lever `atlas:interior-planner = 'off'` keeps the old seat. */

/** The room's plan, as the visit holds it (THE PLANNER IN THE VISIT — interiors programme, slice C, 2026-10-01). */
interface VisitPlan {
  route: ActiveInterior['route'];
  layout: number; pin: LayoutPin | null; pack: number | null;
  descriptor: InteriorDescriptor | null; report: PlanReport | null;
  view: SeatView | null; room: ResidentRoom | null;
  keyedOk: boolean; digestDrift: boolean;
  /** The look's resolved tradition, and the one the drawn descriptor was planned under (C2 item 6). */
  tradition: WiringTradition | null; plannedTradition: WiringTradition | null;
  via: ActiveInterior['planVia']; ms: number;
}

/** A crafted piece's fitted bounds (the master's box + `furnitureFit`), measured once per key per session. */
interface ItemFit { scale: number; yOff: number; min: [number, number, number]; max: [number, number, number] }
/** One LET unit's door in the visit (slice G task 2): who holds the room, whether it is shut for this viewer, the
 *  one-way latch (`null` = not looked at yet), its box's id and whether that box stands, and the two drawn leaves. */
interface UnitDoorState { door: UnitDoor; tenant: string; shut: boolean; pass: boolean | null; cid: string; solid: boolean; shutLeaf: THREE.Group; openLeaf: THREE.Group }


/** (slice H) `localStorage atlas:interior-left-drape = 'off'` → the left stage drape half-turned, as the kit plans it. */
/** (slice H) One press reaches the door by two paths (the E key and the interact action): within this, it is one press. */
const PRESS_DEDUPE_MS = 150;
function leftDrapeMirrored(): boolean {
  try { return typeof localStorage === 'undefined' || localStorage.getItem('atlas:interior-left-drape') !== 'off'; } catch { return true; }
}

export class BuildingInteriorModule implements IModule {
  readonly name = 'BuildingInterior';
  private scene!: THREE.Scene;
  private active: {
    b: EnterableBuilding; spec: InteriorSpec; group: THREE.Group; cids: string[]; entryDist: number; releases: Array<() => void>; packSink: Array<() => void>; packGen: number; look: InteriorLook;
    /** THE PLANNER IN THE VISIT: the lever at entry, the room's plan, the furniture colliders (`int:<id>:furn:…`). */
    plannerOn: boolean; plan: VisitPlan; furnCids: string[]; furnSeq: number; version: number;
    /** The shell's materials (slice F's props and fixtures wear them), every material a prop took (the lazy partition /
     *  lift groups are built on first read and must be freed with the visit), the fixtures of the current plan. */
    mats: ShellMaterials; propMats: Set<THREE.Material>; fixtures: THREE.Group | null;
    /** THE LAST-RESORT GROUND (C2 item 2): this visit's own box (`int:<id>:safety`), the interior's own ('own': the
     *  Hive, a preset), or none (the lever). */
    safety: 'visit' | 'own' | 'off'; safetyCid: string | null;
    /** CRAFTED PIECES COLLIDE TOO (C2 item 3): `int:<id>:item:<itemId>` per placed `structure_item` that blocks walking. */
    itemCids: Map<string, { cid: string; sig: string; floor: number }>;
    /** THE PARTITIONS (slice G): the plan's walls as one group (merged per material) and their THICK boxes `int:<id>:part:<floor>:<n>`. */
    partitions: THREE.Group | null; partCids: string[];
    /** THE TENANT'S DOOR (slice G task 2): the leaves of this visit's LET units (`sig` = what they were built from)
     *  and, per door, the ONE-WAY latch and its thick box `int:<id>:door:<floor>:<n>` (standing only while it blocks). */
    doors: { sig: string; root: THREE.Group | null; items: UnitDoorState[] };
  } | null = null;
  /** The door levers (`platform_flag`, ABSENT = ON) as last read — twice a second, not per frame. */
  private doorLeversAt = 0;
  private doorLeversVal = { rooms: true, rentals: true };
  /** The last "Room … is let" refusal shown (one toast per refusal run, not one per retry). */
  private doorToastAt = 0;
  /** WHO IS WHERE (slice G): the room this client stands in, the last report the server ACCEPTED, the debounce. */
  private presence: { id: string; key: string; since: number; accepted: string; busy: boolean; retryAt: number; sent: number; ok: number; refused: number; lastWhy: string } | null = null;
  /** Indoor rescues this session (counted and logged — C2 item 2), the last one, and the wedge clock. */
  private rescues = 0;
  private lastRescue: { id: string; why: string; dy: number; at: number } | null = null;
  private underSince = 0;
  private rescueAt = 0;
  /** Crafted pieces' fitted bounds by furniture key (`null` = no model: no box; a promise = being measured). */
  private readonly itemFits = new Map<string, ItemFit | null | Promise<ItemFit | null>>();
  /** Pinned rooms no tradition reproduced this session (`<id>|<digest>`): the search is not repeated every entry. */
  private readonly pinMisses = new Set<string>();
  private layoutSubscribed = false;
  /** Structures whose pin digest this client already asked the server to write (once a session each). */
  private readonly stampAsked = new Set<string>();
  private debugOn = false;
  private debugHandle: InteriorDebugHandle | null = null;
  private debugVersion = -1;
  private prompt: HTMLDivElement | null = null;
  private onKey: ((e: KeyboardEvent) => void) | null = null;
  private nearDoorId: string | null = null;
  /** The scan's distance to `nearDoorId`'s door — re-claimed every frame between scans (2026-09-25). */
  private nearDoorDist = 0;
  /** Where that door stands (world x/z): the per-frame claim measures the LIVE distance to it — the scan's number is
   *  up to ~170 ms stale while the player walks, and a stale door won the console's E at the console (2026-09-25). */
  private nearDoorPos: { x: number; z: number } | null = null;
  private focusFrame = 0;
  private acc = 0;
  // ── ENTRY-LATENCY MARKS (interiors programme, slice A, 2026-10-01) — MEASUREMENT ONLY: nothing here changes a visit.
  // `performance.mark('interior:<stage>')` at intent (the E press), shell (meshes in the scene, the exterior hidden),
  // colliders (every `int:` box live in Rapier), first-piece (the first frame after the first furniture piece was
  // seated) and furnished (the entry's furnishing pass returned). `__interior.perf()` reads the LAST visit: ms from
  // intent (null = not reached — a visit left mid-load keeps its nulls) and the counts, by floor.
  private perfIntentAt = 0;
  private perf: {
    id: string; purpose: string; t0: number; floors: number; pieceFrame: number;
    /** `near` (slice H): every piece of the storey the player stood on at the door, ± NEAR_STOREYS, is seated. */
    ms: { shell: number | null; colliders: number | null; firstPiece: number | null; near: number | null; furnished: number | null };
    meshes: number; panes: number; colliders: number; meshesByFloor: number[]; collidersByFloor: number[]; piecesByFloor: number[];
    /** Box colliders of walk-blocking FURNITURE the entry's pass added (slice C, 2026-10-01) — `colliders` stays the shell's. */
    furnitureColliders: number;
    /** Fixtures drawn as their generated MODEL (C2 item 8: the wall sconce) and still as the procedural stand-in. */
    fixtureModels: number; fixtureStandIns: number;
    /** WHERE THE TIME GOES (slice H, 2026-10-02 — measurement only): main-thread ms per stage of the entry, summed;
     *  `loadWait` is WALL time spent awaiting furniture masters (fetch + parse + transcode, the asset gate's queue),
     *  the rest is synchronous work. `frames` = the UI ticks between intent and furnished (the hitch profile). */
    cost: Record<string, number>; frames: { n: number; sum: number; max: number; over50: number; over100: number; last: number };
  } | null = null;
  /** Add `ms` of the entry's work to a named stage (measurement only; a later pack change is not entry latency). */
  private perfCost(stage: string, t0: number): void {
    const p = this.perf;
    if (!p || p.ms.furnished !== null) return;
    p.cost[stage] = (p.cost[stage] ?? 0) + (performance.now() - t0);
  }
  private perfCount(stage: string, n = 1): void {
    const p = this.perf;
    if (!p || p.ms.furnished !== null) return;
    p.cost[stage] = (p.cost[stage] ?? 0) + n;
  }
  /** Stage costs `enter()` measured BEFORE the record exists (the spec, the look, the shell's meshes and colliders). */
  private perfPending: Record<string, number> = {};
  private perfBegin(b: EnterableBuilding, spec: InteriorSpec, purpose: string, t0: number, meshes: number, colliders: number): void {
    const floorOf = (y: number): number => Math.max(0, Math.min(spec.floors - 1, Math.floor(y / FLOOR_H)));
    const zeros = (): number[] => new Array<number>(spec.floors).fill(0);
    const rec = { id: b.id, purpose, t0, floors: spec.floors, pieceFrame: -1, ms: { shell: null, colliders: null, firstPiece: null, near: null, furnished: null },
      meshes, panes: spec.panes.length, colliders, meshesByFloor: zeros(), collidersByFloor: zeros(), piecesByFloor: zeros(), furnitureColliders: 0, fixtureModels: 0, fixtureStandIns: 0,
      cost: this.perfPending, frames: { n: 0, sum: 0, max: 0, over50: 0, over100: 0, last: performance.now() } };
    this.perfPending = {};
    this.perf = rec;
    try { // the user-timing buffer never clears itself: one visit's marks at a time
      for (const st of ['intent', 'shell', 'colliders', 'first-piece', 'near', 'furnished']) performance.clearMarks(`interior:${st}`);
      performance.mark('interior:intent', { startTime: t0, detail: b.id });
    } catch { /* measurement must never break a visit */ }
    this.perfMark('shell');
    if (this.physics.ready) this.perfMark('colliders'); // else Rapier holds them queued — perfFrame marks the frame it wakes
    for (const box of spec.boxes) { if (box.render !== false) rec.meshesByFloor[floorOf(box.y)]++; if (box.collide) rec.collidersByFloor[floorOf(box.y)]++; }
    for (const pane of spec.panes) rec.meshesByFloor[floorOf(pane.y)]++;
  }
  private perfMark(stage: 'shell' | 'colliders' | 'firstPiece' | 'near' | 'furnished'): void {
    const p = this.perf;
    if (!p || p.ms[stage] !== null) return;
    p.ms[stage] = Math.round((performance.now() - p.t0) * 10) / 10;
    try { performance.mark(`interior:${stage === 'firstPiece' ? 'first-piece' : stage}`, { detail: p.id }); } catch { /* as above */ }
  }
  /** One furniture piece seated by the ENTRY's pass (a later pack change is not entry latency). */
  private perfPiece(session: NonNullable<BuildingInteriorModule['active']>, floorY: number): void {
    const p = this.perf;
    if (!p || this.active !== session || p.id !== session.b.id || p.ms.furnished !== null) return;
    p.piecesByFloor[Math.max(0, Math.min(p.floors - 1, Math.round(floorY / FLOOR_H)))]++;
    if (p.pieceFrame < 0) p.pieceFrame = this.focusFrame; // the next UI tick closes the first frame that drew it
  }
  private perfFrame(): void {
    const p = this.perf;
    if (!p || this.active?.b.id !== p.id) return;
    if (p.ms.furnished === null) { // the hitch profile of the entry: every UI tick until the furnishing pass returns
      const now = performance.now(), dt = now - p.frames.last; p.frames.last = now;
      p.frames.n++; p.frames.sum += dt; if (dt > p.frames.max) p.frames.max = dt; if (dt > 50) p.frames.over50++; if (dt > 100) p.frames.over100++;
    }
    if (p.ms.colliders === null && this.physics.ready) this.perfMark('colliders');
    if (p.ms.firstPiece === null && p.pieceFrame >= 0 && this.focusFrame > p.pieceFrame) this.perfMark('firstPiece');
  }
  /** The entry's furnishing pass, still fire-and-forget; when it returns with the visit still open, the last mark. */
  private perfFurnish(pass: Promise<void>): void {
    const p = this.perf;
    void pass.then(() => { if (p && this.perf === p && this.active?.b.id === p.id) { this.perfMark('near'); this.perfMark('furnished'); } }); // a room with no near piece is near-complete when it is complete
  }
  private perfReport(): Record<string, unknown> | null {
    const p = this.perf;
    if (!p) return null;
    return { id: p.id, purpose: p.purpose, inside: this.active?.b.id === p.id, floors: p.floors, ms: { ...p.ms },
      meshes: p.meshes, panes: p.panes, colliders: p.colliders, furnitureColliders: p.furnitureColliders, pieces: p.piecesByFloor.reduce((a, c) => a + c, 0),
      fixtureModels: p.fixtureModels, fixtureStandIns: p.fixtureStandIns,
      // (slice H) where the entry's time went, its frames, and what the door prompt had prewarmed for it
      prewarm: this.prewarmFor === p.id ? { ...this.prewarmLast } : null,
      cost: Object.fromEntries(Object.entries(p.cost).map(([k, v]) => [k, Math.round(v * 10) / 10])),
      frames: { n: p.frames.n, meanMs: p.frames.n ? Math.round(p.frames.sum / p.frames.n * 10) / 10 : 0, maxMs: Math.round(p.frames.max * 10) / 10, over50: p.frames.over50, over100: p.frames.over100 },
      // (slice G) the visit's partitions (walls of the plan) and their thick boxes — beside the shell's `colliders`
      partitions: this.active?.b.id === p.id ? ((this.active.partitions?.userData.partitionCount as number | undefined) ?? 0) : 0,
      partitionColliders: this.active?.b.id === p.id ? this.active.partCids.length : 0,
      byFloor: p.piecesByFloor.map((pieces, floor) => ({ floor, meshes: p.meshesByFloor[floor], colliders: p.collidersByFloor[floor], pieces })) };
  }

  constructor(private readonly physics: PhysicsModule, private readonly assets?: AssetModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    const prompt = document.createElement('div');
    prompt.id = 'interior-prompt';
    prompt.style.cssText = 'position:fixed;left:50%;bottom:228px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg2) 85%, transparent);border:1px solid var(--ax-line2);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(prompt);
    this.prompt = prompt;
    this.onKey = (e: KeyboardEvent): void => {
      if (!keyIs(e.code, 'interact') || e.ctrlKey || e.metaKey || e.repeat) return; // repeat guard: holding E must not toggle enter/exit
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      this.doorPress();
    };
    // THE PAD AND THE REBOUND KEY (slice H, 2026-10-02 — item 5): the interact ACTION (a pad's button, a rebound key,
    // touch) arrives as `atlas:place-requested` — the lift already answered it, the door did not: a pad player could
    // not walk into a building. One press is one press: E reaches both paths, so a press within PRESS_DEDUPE_MS of the
    // last is the same one.
    bus.on('atlas:place-requested', () => this.doorPress());
    window.addEventListener('keydown', this.onKey);
    this.initRest(app);
  }

  private lastPress = 0;
  private doorPress(): void {
    const now = performance.now();
    if (now - this.lastPress < PRESS_DEDUPE_MS) return;
    this.lastPress = now;
    {
      if (this.active) {
        // ONE KEY, ONE FOCUS (2026-09-24): the arbiter says whose press this is.
        if (!interactFocus().owns('door')) return;
        if (this.nearDoorId === 'exit') this.exit();
        return;
      }
      if (this.nearDoorId) {
        if (!interactFocus().owns('door')) return;
        const b = enterables.get(this.nearDoorId);
        if (!b) return;
        // THE DOOR'S RULE (2026-09-23): a player structure's console sets who may walk in.
        const gate = this.doorGate(b);
        if (!gate.ok) { bus.emit('atlas:toast', { text: gate.why, kind: 'error' }); return; }
        this.perfIntentAt = performance.now(); // entry-latency mark: intent (measurement only)
        this.enter(b);
      }
    }
  }

  private initRest(app: App): void {
    // The structure console redeeded the building under the player: the shell goes with the exterior.
    bus.on('atlas:interior-exit', () => { if (this.active) this.exit(true); });
    // THE FURNISH PACKS: a deed's pack row changed under the player — re-seat the pack's furniture.
    // The guild hall keeps its own furnishing (2026-09-25): a pack row on a hall (the console used to sell one) must not
    // seat a pack over the hall's own pieces.
    bus.on('atlas:interior-refurnish', ({ structureId }) => { if (this.active?.b.id === `deed:${structureId}` && this.active.b.key !== 'deed:guild_hall') void this.refurnishDeed(); });
    // THE LAYOUT PIN (slice C): the pins are a small public table every visitor needs (a pinned house is planned at ITS
    // revision); a pin moving under the player re-seats the room when the LAYOUT changed (a digest stamp does not).
    bus.on('atlas:account-ready', () => this.subscribeLayout());
    if (atlas.conn) this.subscribeLayout();
    app.world.addSystem(makeSystem('BuildingInterior', Phase.UI, 34, () => this.tick()));
    // Dev hooks (probes) — list/enter/exit without walking.
    (window as unknown as Record<string, unknown>).__interior = {
      list: (): Array<{ id: string; name: string; purpose: InteriorPurpose; w: number; h: number; d: number; floors: number; x: number; z: number; groundY: number; yaw: number }> =>
        [...enterables.values()].map((b) => ({ id: b.id, name: b.name, purpose: resolvePurpose(b.id, b.key).purpose, w: Math.round(b.w), h: Math.round(b.h), d: Math.round(b.d), floors: interiorSpec(b.w, b.h, b.d).floors, x: b.x, z: b.z, groundY: b.groundY, yaw: b.yaw })),
      enter: (id: string): boolean => { const b = enterables.get(id); if (!b || this.active) return false; this.enter(b); return true; },
      exit: (): boolean => { if (!this.active) return false; this.exit(); return true; },
      perf: () => this.perfReport(), // the last visit's entry-latency marks + counts (slice A)
      /** (slice H) What the door prompt does when it stands on `id`: plan the room, ask for its masters. Resolves once
       *  the PLAN is in the cache and the asks are out (the loads themselves keep running). Beds and probes have no walk-up. */
      prewarm: async (id: string): Promise<unknown> => {
        if (this.active || !interiorPrewarmEnabled()) return { ok: false, why: this.active ? 'inside' : 'lever' };
        this.prewarmDoor(null);
        const w = (this.prewarm = { id, since: performance.now() - PREWARM_DWELL_MS, started: true, settle: null as (() => void) | null });
        const ok = await this.prewarmPass(w);
        return { ok, ...this.prewarmLast, count: this.prewarmCount };
      },
      // THE ACTIVE DESCRIPTOR (slice C): what the room IS — units, floor links, the lift candidate and the cover fields
      // ride in it, unused until waves 3–4. `planner()` says how the room was planned and what was seated.
      descriptor: (): InteriorDescriptor | null => this.active?.plan.descriptor ?? null,
      planner: () => this.plannerReport(),
      pieces: () => this.pieceReport(),
      /** THE TENANT'S DOOR (slice G task 2): every let unit's door of the visit, its latch and its box. */
      doors: () => this.doorReport(),
      /** Dev-gated: draw the planner's debug view over the live room (zones, anchors, routes, reserved regions, the
       *  digest, why a candidate lost). Returns what it drew, or false when not allowed / not inside / nothing planned. */
      debug: (on = true): unknown => this.setDebug(!!on),
      state: (): { inside: string | null; purpose: InteriorPurpose | null; floors: number; colliders: number; panes: number; sign: boolean; furniture: number; rescues: number; safety: string; itemColliders: number; lastRescue: unknown } | null => this.active
        ? { inside: this.active.b.id, purpose: resolvePurpose(this.active.b.id, this.active.b.key).purpose, floors: this.active.spec.floors, colliders: this.active.cids.length, panes: this.active.spec.panes.length,
            sign: !!this.active.group.getObjectByName('interior-sign'), furniture: this.active.group.children.filter((o) => o.userData.furnKey).length,
            rescues: this.rescues, safety: this.active.safety, itemColliders: this.active.itemCids.size, lastRescue: this.lastRescue }
        : { inside: null, purpose: null, floors: 0, colliders: 0, panes: 0, sign: false, furniture: 0, rescues: this.rescues, safety: 'none', itemColliders: 0, lastRescue: this.lastRescue },
    };
  }

  // ── Exterior door markers: a warm glowing frame on every ENTERABLE door so
  // players can tell from the street which buildings open (and which don't).
  private readonly doorMarkers = new Map<string, THREE.Group>();
  private markerMats: { jamb: THREE.MeshBasicMaterial; portal: THREE.MeshBasicMaterial } | null = null;
  private markerAcc = 0;

  private markersTick(): void {
    if (++this.markerAcc % 60 !== 0) return; // ~1 Hz — doors don't move
    const px = atlas.localX, pz = atlas.localZ;
    const RANGE = 130;
    this.markerMats ??= {
      jamb: new THREE.MeshBasicMaterial({ color: 0xffd9a0, toneMapped: false }),
      portal: new THREE.MeshBasicMaterial({ color: 0xffb85c, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false, toneMapped: false }),
    };
    const live = new Set<string>();
    for (const b of enterables.values()) {
      if (Math.abs(b.x - px) > RANGE || Math.abs(b.z - pz) > RANGE) continue;
      if (this.active?.b.id === b.id) continue; // exterior hidden — no frame
      live.add(b.id);
      if (this.doorMarkers.has(b.id)) continue;
      const dw = doorWorld(b);
      const gI = new THREE.Group();
      const jambGeo = new THREE.BoxGeometry(0.18, 2.6, 0.18);
      const lintelGeo = new THREE.BoxGeometry(2.26, 0.18, 0.18);
      const l = new THREE.Mesh(jambGeo, this.markerMats.jamb); l.position.set(-1.04, 1.3, 0);
      const r = new THREE.Mesh(jambGeo, this.markerMats.jamb); r.position.set(1.04, 1.3, 0);
      const top = new THREE.Mesh(lintelGeo, this.markerMats.jamb); top.position.set(0, 2.69, 0);
      const portal = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 2.6), this.markerMats.portal);
      portal.position.set(0, 1.3, 0);
      gI.add(l, r, top, portal);
      // Sit at the door, facing outward, nudged off the facade (no z-fight).
      gI.position.set(dw.x, b.groundY, dw.z);
      gI.rotation.y = b.yaw;
      gI.translateZ(-0.22);
      this.scene.add(gI);
      this.doorMarkers.set(b.id, gI);
    }
    for (const [id, gM] of this.doorMarkers) {
      if (live.has(id)) continue;
      this.scene.remove(gM);
      gM.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); });
      this.doorMarkers.delete(id);
    }
  }

  /** The current world's kind, cached per world id (a world's kind never
   *  changes). The scan walked the whole replicated `world` table TWICE per
   *  6 Hz tick — every account seeds one, so a player in a late personal world
   *  visited nearly every row (bug sweep 2026-09-23). A row not replicated yet
   *  is asked again on the next scan, never latched as "unknown". */
  private worldKindCache: { id: bigint; kind: string } | null = null;
  private currentWorldKind(): string {
    const id = atlas.currentWorldId;
    if (id === null || !atlas.conn) return '';
    if (this.worldKindCache?.id === id) return this.worldKindCache.kind;
    let kind = '';
    try { kind = atlas.conn.db.world.worldId.find(id)?.kind ?? ''; } catch { /* table mid-churn */ }
    if (kind) this.worldKindCache = { id, kind };
    return kind;
  }

  private tick(): void {
    const focus = interactFocus();
    focus.tick(++this.focusFrame); // the first UI system of the frame flips the arbiter
    this.perfFrame(); // entry-latency marks (measurement only)
    if (this.debugOn) this.debugTick(); // the planner's overlay follows the room's plan (dev-gated)
    this.markersTick();
    if (this.active) this.rescueTick(); // THE INDOOR RESCUE (C2): every frame — a fall is fast
    if (this.active) this.presenceTick(); // WHO IS WHERE (slice G): one report per room change, debounced
    if (this.active) this.doorTick();     // THE TENANT'S DOOR (slice G task 2): the let units' leaves follow the lease rows
    if (++this.acc % 10 === 0) this.scan(); // ~6 Hz: the nearest door (the scan is the cost)
    if (this.active && this.acc % 30 === 15) this.syncItemColliders(this.active); // ~2 Hz: placed, moved and picked-up crafted pieces
    // THE CLAIM IS PER FRAME (2026-09-25): claims live ONE frame, and the door claimed only on the 6 Hz scan frames —
    // E at a door worked one frame in ten and the "E — leave" pill almost never showed. The scan's result is
    // re-claimed every frame, and the pill follows the arbiter every frame (a console or a seat can win it).
    if (this.nearDoorId) {
      const dist = this.nearDoorPos ? Math.hypot(atlas.localX - this.nearDoorPos.x, atlas.localZ - this.nearDoorPos.z) : this.nearDoorDist;
      focus.claim({ id: 'door', dist, priority: 0, label: this.nearDoorId === 'exit' ? 'leave' : 'enter' });
    }
    if (this.prompt) {
      const show = !!this.nearDoorId && focus.owns('door');
      if ((this.prompt.style.display !== 'none') !== show) this.prompt.style.display = show ? 'block' : 'none';
    }
  }

  /** The 6 Hz half of the tick: interior upkeep and the nearest door (`nearDoorId` / `nearDoorDist` / the pill's text);
   *  the per-frame half claims it. */
  private scan(): void {
    // Interiors live wherever enterables are registered: the Commons AND the
    // Merchant Hive (its 172 leaseable stores are all EnterableBuildings —
    // the commons-only gate silently disabled E-enter on the hive).
    const kind = this.currentWorldKind();
    const onGround = atlas.currentWorldId !== null && !atlas.spaceFlight
      && (atlas.currentWorldId === atlas.commonsWorldId || kind === 'merchant' || kind === 'personal');
    if (!onGround) {
      if (this.active) this.exit(); // left the world (teleport/space) — restore
      this.prewarmDoor(null);
      this.nearDoorId = null; // the per-frame claim must not outlive the world (2026-09-25)
      if (this.prompt) this.prompt.style.display = 'none';
      return;
    }
    const px = atlas.localX, pz = atlas.localZ;
    if (this.active) {
      this.prewarmDoor(null); // (slice H) inside: the visit holds its own handles; the prompt's are released a moment later
      // Settlement rebuild mid-visit (LOD flip / re-stream): the OLD design
      // exited the interior here — which DELETED every interior collider
      // while the player stood mid-stairs (glitch through the steps, limbo,
      // terrain clamp + restored exterior = ejected: the whole field-bug
      // trio, re-created every re-assembly and bypassing all the geometry
      // fixes). The interior now PERSISTS through rebuilds: the shell and
      // colliders stay alive; when the fresh registry entry appears we
      // re-hide the rebuilt exterior and adopt its closures for the eventual
      // restore. While the entry is missing (mid-rebuild) we simply hold.
      const cur = enterables.get(this.active.b.id);
      if (!cur) {
        // A MARKER that vanished is not a rebuild (2026-09-23): Edit Mode unflagged it or deleted its part, and its
        // entry never comes back — holding kept the visitor in a ghost interior (the walk-out and auto-exit checks
        // skipped, E dead away from the door, the part hidden) until they left the world. Leave as a world change does.
        if (this.active.b.id.startsWith('mark:')) { this.exit(); return; }
        this.nearDoorId = null; // no door to claim mid-rebuild (2026-09-25: the claim is per frame now)
        if (this.prompt) this.prompt.style.display = 'none';
        return; // mid-rebuild — hold; interior geometry remains solid
      }
      if (cur !== this.active.b) {
        cur.hide();            // strip the rebuilt exterior + its collider
        this.active.b = cur;   // fresh closures own the eventual restore
      }
      const b = this.active.b;
      const dw = doorWorld(b);
      const dDoor = Math.hypot(dw.x - px, dw.z - pz);
      const dCenter = Math.hypot(b.x - px, b.z - pz);
      // Walked OUT (door gap or any other way past the walls) → restore the
      // exterior IMMEDIATELY: the shell must never be seen standing in the
      // street ("the building was replaced" bug). Local-frame test against
      // the shell's own core bounds; a margin avoids doorway flicker.
      const c = Math.cos(b.yaw), sn = Math.sin(b.yaw);
      const dx = px - b.x, dz = pz - b.z;
      const lx = c * dx - sn * dz, lz = sn * dx + c * dz;
      // Real door entries start within the walkable CORE (clamped bounds —
      // the raw footprint of a km-bbox landmark would count remote dev-hook
      // entries as "at the door" and instantly threshold-exit them).
      const enteredAtDoor = this.active.entryDist < Math.hypot(this.active.spec.hw, this.active.spec.hd) + 4;
      // Margin clears the 1.6 m thick wall/door tunnel before restoring.
      const walkedOut = enteredAtDoor
        && (Math.abs(lx) > this.active.spec.hw + 1.9 || Math.abs(lz) > this.active.spec.hd + 1.9);
      // Strayed — restore the world. The threshold is entry-distance aware:
      // a real player enters at the door (entryDist ≈ footprint), but the
      // __interior dev hook enters from anywhere — a fixed radius would
      // auto-exit the very next tick and read as "enter never happened".
      const exitAt = Math.max(Math.max(b.w, b.d) / 2 + AUTO_EXIT_M, this.active.entryDist + 12);
      if (walkedOut || dCenter > exitAt) { this.exit(); return; }
      this.nearDoorId = dDoor < DOOR_RANGE_M ? 'exit' : null;
      this.nearDoorDist = dDoor;
      this.nearDoorPos = { x: dw.x, z: dw.z };
      // ONE KEY, ONE FOCUS (2026-09-24): the door claims the key with its distance (every frame, in tick); the console
      // and the furniture claim theirs; the nearest wins the pill and the press.
      if (this.prompt) {
        const text = `E — leave ${this.nameOf(this.active.b)}`;
        if (this.prompt.textContent !== text) this.prompt.textContent = text;
      }
      return;
    }
    // Nearest enterable door within reach.
    let bestId: string | null = null;
    let bestD = DOOR_RANGE_M;
    let bestPos: { x: number; z: number } | null = null;
    for (const b of enterables.values()) {
      if (Math.abs(b.x - px) > 60 || Math.abs(b.z - pz) > 60) continue; // cheap reject
      const dw = doorWorld(b);
      const d = Math.hypot(dw.x - px, dw.z - pz);
      if (d < bestD) { bestD = d; bestId = b.id; bestPos = { x: dw.x, z: dw.z }; }
    }
    this.nearDoorId = bestId;
    this.nearDoorDist = bestD;
    this.nearDoorPos = bestPos;
    this.prewarmDoor(bestId); // (slice H) the room behind the prompt is planned and its masters asked before E
    // The pill's words here; whether it shows is the arbiter's, every frame (tick) — a terminal or a fire nearer than
    // the door now wins the pill as it already won the press (2026-09-25).
    if (this.prompt && bestId) {
      const b = enterables.get(bestId)!;
      const floors = interiorSpec(b.w, b.h, b.d).floors;
      const text = `E — enter ${this.nameOf(b)}${purposeSuffix(b.name, resolvePurpose(b.id, b.key))}${floors > 1 ? ` (${floors} floors)` : ''}`;
      if (this.prompt.textContent !== text) this.prompt.textContent = text;
    }
  }

  /** A deed's console name when set, else the registered name. */
  private nameOf(b: EnterableBuilding): string {
    if (!b.id.startsWith('deed:')) return b.name;
    const db = (atlas.conn?.db as unknown as { structureMeta?: { structureId: { find(id: bigint): { name: string } | undefined } } } | undefined);
    try { return structureDisplayName(db?.structureMeta?.structureId.find(BigInt(b.id.slice(5)))?.name, b.name); } catch { return b.name; }
  }

  /** The door's rule for a player structure: the console's entry policy + the access list (owner always). */
  private doorGate(b: EnterableBuilding): { ok: boolean; why: string } {
    if (!b.id.startsWith('deed:')) return { ok: true, why: '' };
    const conn = atlas.conn;
    if (!conn?.identity) return { ok: true, why: '' };
    const id = BigInt(b.id.slice(5));
    const db = conn.db as unknown as {
      structure: { structureId: { find(i: bigint): { owner: { isEqual(o: unknown): boolean } } | undefined } };
      structureMeta?: { structureId: { find(i: bigint): { entry: string } | undefined } };
      structurePermission: { iter(): Iterable<{ structureId: bigint; subject: { isEqual(o: unknown): boolean }; level: string }> };
    };
    const s = db.structure.structureId.find(id);
    if (!s) return { ok: true, why: '' };
    const me = conn.identity;
    let level: PermLevel | null = null;
    for (const p of db.structurePermission.iter()) if (p.structureId === id && p.subject.isEqual(me)) { level = p.level as PermLevel; break; }
    return canEnterStructure({ isOwner: s.owner.isEqual(me), level, entry: db.structureMeta?.structureId.find(id)?.entry ?? 'public' });
  }

  private enter(b: EnterableBuilding): void {
    const perfT0 = this.perfIntentAt || performance.now(); this.perfIntentAt = 0; // entry-latency mark: intent (the dev hook has no E press)
    const tSpec = performance.now();
    const spec = interiorSpec(b.w, b.h, b.d, b.key);
    const cost: Record<string, number> = { spec: performance.now() - tSpec }; // (slice H) where the entry's time goes — measurement only
    let tMesh = 0, tCol = 0, tm = 0;
    const group = new THREE.Group();
    group.name = 'building-interior';
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const toWorld = (lx: number, lz: number): { x: number; z: number } =>
      ({ x: b.x + lx * cos + lz * sin, z: b.z - lx * sin + lz * cos });
    const purpose = resolvePurpose(b.id, b.key);
    const look: InteriorLook = { id: b.id, key: b.key, purpose: purpose.purpose, x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY, floors: spec.floors, hw: spec.hw, hd: spec.hd }; // what the VISIT knows; shellMaterials completes it from the rows (who built it, the owner's name) — modules/interior/interior_look.ts
    tm = performance.now();
    const mats = shellMaterials(look);
    cost.look = performance.now() - tm;
    setIndoorBodyLight(bodyLightOf(styleFor(look))); // (slice H) a body in this room wears the room's light (lib/indoor_body.ts)
    const cids: string[] = [];
    let ci = 0;
    // THE SHELL AS A HANDFUL OF DRAWS (slice H, 2026-10-02 — interior_shell_batch.ts): with the lever on, the boxes merge
    // per material and the panes are one instanced batch under a root at the building's origin; the loops below then
    // add only the COLLIDERS (unchanged, box for box). `atlas:interior-merge = 'off'` = one mesh per box and pane.
    const merge = interiorMergeEnabled();
    for (const box of spec.boxes) {
      tm = performance.now();
      if (box.render !== false && !merge) {
        const geo = new THREE.BoxGeometry(box.w, box.h, box.d);
        // World-scale tiling (~2.5 m per texture tile): BoxGeometry UVs are
        // 0..1 per face, so scale them by the box's dominant-face dims.
        if (box.mat === 'floor' || box.mat === 'wall' || box.mat === 'stair') {
          const su = Math.max(box.w, box.d) / 2.5;
          const sv = (box.mat === 'floor' ? Math.min(box.w, box.d) : Math.max(box.h, 0.4)) / 2.5;
          const uv = geo.attributes.uv as THREE.BufferAttribute;
          for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
          uv.needsUpdate = true;
        }
        const mesh = new THREE.Mesh(geo, mats[box.mat]);
        const wp = toWorld(box.x, box.z);
        mesh.position.set(wp.x, b.groundY + box.y, wp.z);
        mesh.rotation.y = b.yaw;
        mesh.layers.enable(RAIN_SHELTER_LAYER); // THE ROOF-INDOORS LAW: the shell is the roof the rain map sees while the exterior hides
        group.add(mesh);
      }
      tMesh += performance.now() - tm; tm = performance.now();
      if (box.collide) {
        const wp = toWorld(box.x, box.z);
        const cid = `int:${b.id}:${ci++}`;
        this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [box.w / 2, box.h / 2, box.d / 2] },
          { x: wp.x, y: b.groundY + box.y, z: wp.z, yaw: b.yaw, scale: 1 });
        cids.push(cid);
      }
      tCol += performance.now() - tm;
    }
    tm = performance.now();
    if (merge) group.add(buildShellBatches(spec, mats, { x: b.x, groundY: b.groundY, z: b.z, yaw: b.yaw }).root);
    else for (const pane of spec.panes) {
      const geo = new THREE.PlaneGeometry(pane.w, pane.h);
      const mesh = new THREE.Mesh(geo, mats.glass);
      const wp = toWorld(pane.x, pane.z);
      mesh.position.set(wp.x, b.groundY + pane.y, wp.z);
      // Pane faces its wall normal: x-axis walls face ±X (rotate 90°).
      mesh.rotation.y = b.yaw + (pane.axis === 'x' ? Math.PI / 2 : 0);
      group.add(mesh);
    }
    cost.shellMeshes = tMesh + (performance.now() - tm); cost.shellColliders = tCol;
    // THE GROUND-OF-LAST-RESORT LAW, FOR EVERY INTERIOR (C2 item 2; it was the Hive's and the presets' — interiors.md
    // §4.6). The ground floor's slab is 8 cm and the terrain clamp stands down indoors: a capsule that arrives under
    // the slab (a snap that keeps Y, a chunk rebuilding its trimesh under the door corridor) fell for ever with the
    // visit still active. One box, 2 m thick, its top 10 cm under the pad, out to the walked-out margin; not counted
    // among the shell's colliders. Lever: `atlas:interior-safety = 'off'`.
    let safety: 'visit' | 'own' | 'off' = 'off', safetyCid: string | null = null;
    if (interiorSafetyEnabled()) {
      if (ownsLastResortGround(b.id, this.currentWorldKind())) safety = 'own';
      else {
        const sb = safetyGroundBox(spec);
        safetyCid = `int:${b.id}:safety`;
        this.physics.addStaticCollider(safetyCid, { shape: 'box', halfExtents: [sb.hx, sb.hy, sb.hz] }, { x: b.x, y: b.groundY + sb.y, z: b.z, yaw: b.yaw, scale: 1 });
        safety = 'visit';
      }
    }
    this.underSince = 0;
    group.userData.interiorId = b.id; // the pane-ownership tag the area lights read (render wave 1, 2026-09-17)
    tm = performance.now();
    this.scene.add(group);
    b.hide();
    cost.hide = performance.now() - tm;
    this.perfPending = cost;
    const entryDist = Math.hypot(b.x - atlas.localX, b.z - atlas.localZ);
    // THE PLANNER IN THE VISIT (slice C): the lever is read at the door (`atlas:interior-planner = 'off'` = today's
    // furnishers, today's sign seat); the plan itself lands a moment later — the shell and its colliders never wait.
    const plannerOn = interiorPlannerEnabled();
    this.active = {
      b, spec, group, cids, entryDist, releases: [], packSink: [], packGen: 0, look, plannerOn, furnCids: [], furnSeq: 0, version: 0, mats, propMats: new Set(), fixtures: null, partitions: null, partCids: [], doors: { sig: '', root: null, items: [] },
      safety, safetyCid, itemCids: new Map(),
      plan: { route: plannerOn ? 'pending' : legacyFurnisherFor(b.id, b.key), layout: 0, pin: null, pack: null, descriptor: null, report: null, view: null, room: null, keyedOk: true, digestDrift: false, tradition: null, plannedTradition: null, via: 'none', ms: 0 },
    };
    atlas.interiorId = b.id;
    this.publishActive(this.active);
    // (slice H) the shell's LOGICAL size — one per drawn box and pane, whether or not they are batched into a few draws
    const shellDrawn = spec.boxes.reduce((n, box) => n + (box.render !== false ? 1 : 0), 0) + spec.panes.length;
    this.perfBegin(b, spec, purpose.purpose, perfT0, shellDrawn, cids.length); // entry-latency marks: shell + colliders
    this.nearDoorId = null; // the outside door's claim ends at the threshold; the next scan claims the way out (2026-09-25)
    // Versona homes (settlement buildings) arrive furnished — the versona
    // smiths craft from the same slate players do (crafting_ops events tell
    // the story); the layout is deterministic per building.
    // THE PURPOSE LAW (2026-09-22): deeds and markers — bare until now —
    // furnish by their resolved purpose; the bespoke hall and store keep
    // their own plans; settlement homes keep the resident mirror.
    // SLICE C (2026-10-01): with the planner on, EVERY namespace furnishes through `planInterior` (a civic tower
    // stops being a bedroom; a deed follows its layout pin); the lever keeps the five furnishers below, verbatim.
    this.perfFurnish(this.furnishFor(this.active, toWorld, purpose.purpose));
    tm = performance.now();
    this.placeSign(b, spec, group, toWorld, purpose);
    this.perfCost('sign', tm);
    bus.emit('atlas:status', { text: `${this.nameOf(b)}${purposeSuffix(b.name, purpose)} — ${spec.floors} floor${spec.floors > 1 ? 's' : ''}; the stairs climb from the west wall. E at the door to leave. [r10]` });
  }

  // ─── THE PLANNER IN THE VISIT (the interiors programme, slice C, 2026-10-01) ─────────────────────────────────────

  /** TODAY'S DISPATCH — the same five furnishers `enter` has always picked between, by the same tests in the same
   *  order (`legacyFurnisherFor`). The lever's whole path, and the bespoke rooms' while their archetype is `legacy.*`. */
  private furnishLegacy(
    kind: LegacyFurnisher, b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number }, purpose: InteriorPurpose,
  ): Promise<void> {
    if (kind === 'home') return this.furnishVersonaHome(b, spec, group, toWorld);
    if (kind === 'guild') return this.furnishGuildHall(b, spec, group, toWorld);
    if (kind === 'hive') return this.furnishHiveStore(b, spec, group, toWorld);
    if (kind === 'pack') return this.furnishDeed(b, spec, group, toWorld, purpose); // by the bought pack, else EMPTY
    return this.furnishByPurpose(b, spec, group, toWorld, purpose);
  }

  /** Furnish the visit: the lever → today's furnisher; else the room's PLAN (cached, off this frame), seated exactly
   *  as `placeSlot` seats a slot. A deed is planned at ITS PIN (no row = layout 0 = today's plan and today's keys). */
  private async furnishFor(
    session: NonNullable<BuildingInteriorModule['active']>,
    toWorld: (lx: number, lz: number) => { x: number; z: number }, purpose: InteriorPurpose,
  ): Promise<void> {
    const { b, spec, group } = session;
    this.dropPartitions(session); // (slice G) a re-plan rebuilds them; the lever and a legacy room have none
    const legacy = legacyFurnisherFor(b.id, b.key);
    const packInterior = isPackInterior(b.id, b.key);
    const pin = packInterior ? this.pinOf(b) : null;
    const pack = packInterior ? this.deedPack(b) : null;
    const gen = session.packGen;
    if (!session.plannerOn) {
      // THE LEVER. One layout must never be keyed as another: a structure pinned ≥ 1 drawn as the classic room offers
      // no keyed function on its pack clones (what it stores stays reachable from the console's list).
      session.plan = { ...session.plan, route: legacy, layout: 0, pin, pack: pack?.pack ?? null, keyedOk: !(pin && pin.layoutRevision >= 1), descriptor: null, report: null, view: null, room: null, digestDrift: false, tradition: null, plannedTradition: null, via: 'none', ms: 0 };
      this.publishActive(session);
      return this.furnishLegacy(legacy, b, spec, group, toWorld, purpose);
    }
    const layout = layoutFor(b.id, b.key, pin);
    // A ROOM'S FURNITURE FOLLOWS ITS TRADITION, IN EVERY NAMESPACE (C2 item 6): the LOOK's resolved tradition — the
    // shell already wears it (`shellMaterials` completed the look at the door). The raw look carries none, so before
    // C2 every plan took the planner's default and a deed in a Borean city was furnished as nobody's.
    const resolved: WiringTradition = styleFor(session.look).tradition;
    session.plan = { ...session.plan, route: 'pending', layout, pin, pack: pack?.pack ?? null, tradition: resolved };
    this.publishActive(session);
    let descriptor: InteriorDescriptor, report: PlanReport, via: ActiveInterior['planVia'], ms: number, todayDigest: string;
    const tPlan = performance.now();
    try {
      let planned = await planRoom(planInputFor(b, purpose, layout, resolved), spec);
      todayDigest = planned.result.descriptor.digest;
      // A PINNED ROOM DOES NOT MOVE: its digest covers the tradition it was pinned under. When today's plan is not the
      // pin (its city changed tradition; a pin stamped before C2), the other traditions are tried in a fixed order and
      // the one that reproduces the digest IS the room — drawn as pinned, flagged as drift, moved only by a Refit.
      if (pin && pinHasDigest(pin) && layout === pin.layoutRevision && todayDigest !== pin.digest && !this.pinMisses.has(`${b.id}|${pin.digest}`)) {
        let found = false;
        for (const t of pinnedTraditionOrder(resolved).slice(1)) {
          const alt = await planRoom(planInputFor(b, purpose, layout, t), spec);
          if (alt.result.descriptor.digest === pin.digest) { planned = { ...alt, ms: planned.ms + alt.ms }; found = true; break; }
        }
        if (!found) this.pinMisses.add(`${b.id}|${pin.digest}`); // a planner / kit change under the pin: today's plan, with drift
      }
      descriptor = planned.result.descriptor; report = planned.result.report; via = planned.via; ms = planned.ms;
    } catch (e) {
      // The planner must never leave a room bare: today's furnisher stands in, and the overlay says why.
      console.warn(`[BuildingInterior] planning ${b.id} failed — the legacy furnisher stands in`, e);
      if (this.active !== session || session.packGen !== gen) return;
      session.plan = { ...session.plan, route: legacy, keyedOk: layout === 0 };
      this.publishActive(session);
      return this.furnishLegacy(legacy, b, spec, group, toWorld, purpose);
    }
    if (this.active !== session || session.packGen !== gen) return; // THE SESSION LAW: left, re-entered, or re-furnished meanwhile
    this.perfCost('planWait', tPlan);
    const route = furnishRouteFor(b.id, b.key, true, descriptor);
    const detail = clampDetail(session.look.detail ?? activePreset().interiorDetail) as DetailLevel;
    const view = route === 'descriptor' ? seatView(descriptor, { pack: packInterior ? pack : undefined, detail }) : null;
    session.plan = {
      route, layout, pin, pack: pack?.pack ?? null, descriptor, report, view, room: view ? residentRoomOf(descriptor, view) : null,
      // DRIFT = today's plan for this room is not what the pin holds (whether or not the pinned room was found)
      keyedOk: true, digestDrift: !!pin && pin.layoutRevision >= 1 && pin.digest !== '' && pin.digest !== todayDigest,
      tradition: resolved, plannedTradition: (descriptor.recipe.tradition as WiringTradition | undefined) ?? null, via, ms: Math.round(ms * 10) / 10,
    };
    this.publishActive(session);
    this.stampDigest(session);
    void this.syncItemColliders(session); // crafted pieces collide too — now that the planned floors' counts are known
    if (route !== 'descriptor' || !view) return this.furnishLegacy(route as LegacyFurnisher, b, spec, group, toWorld, purpose);
    let tStage = performance.now();
    this.placePartitions(session, toWorld, descriptor); // (slice G) the rooms' walls — thick boxes, open doorways
    this.perfCost('partitions', tStage); tStage = performance.now();
    this.placeFixtures(session, toWorld, descriptor, purpose, detail); // the shell's fixtures follow the PLAN (never a legacy one)
    this.perfCost('fixtures', tStage); tStage = performance.now();
    // THE STOREY YOU STAND ON FIRST (slice H, 2026-10-02). MEASURED: 89–98 % of a cold door is waiting for furniture
    // masters, and the seating walked the plan's order one awaited piece after another — a piece whose master had
    // landed waited behind one whose master had not. Now every shown piece is asked for in `seatOrder` (the storey the
    // player stands on, then outward) and each is seated the moment ITS master lands; the far storeys fill in behind
    // floor slabs. The set is the plan-order loop's own (THE DETAIL RULE: `shown` is a render filter — the plan, the
    // digest and every pack rank are unchanged). `atlas:interior-stream = 'off'` = the plan's order, one by one.
    const streamed = interiorStreamEnabled();
    const here = storeyAt(atlas.localY - b.groundY, spec.floors, FLOOR_H);
    const settle = this.prefetchFurniture(view, streamed ? here : null);
    this.perfCost('prefetchAsk', tStage);
    const order: SeatPiece[] = streamed ? seatOrder(view, here) : view.floors.flatMap((fl) => fl.pieces.filter((sp) => sp.shown));
    let nearLeft = order.reduce((n, sp) => n + (Math.abs(sp.floor - here) <= NEAR_STOREYS ? 1 : 0), 0);
    const landed = (sp: SeatPiece): void => { if (Math.abs(sp.floor - here) <= NEAR_STOREYS && --nearLeft === 0 && this.active === session && session.packGen === gen) this.perfMark('near'); };
    if (nearLeft === 0) this.perfMark('near');
    const seats: Array<Promise<boolean>> = [];
    try {
      for (const sp of order) {
        if (session.packGen !== gen) return; // a newer pack / pin took over
        if (isKitProp(sp.key)) landed(sp); // (slice H) a prop is built on the line below, not loaded: it has landed
        if (isKitProp(sp.key)) { this.placeProp(session, toWorld, sp, packInterior); continue; } // slice F's procedural pieces: boxes, no load
        const seat = this.placeSlot(session, b, group, toWorld, sp.key, sp.x, sp.z, sp.yaw, sp.y, 0, {
          sink: packInterior ? session.packSink : session.releases, tag: packInterior ? 'packFurniture' : undefined,
          floor: sp.floor, index: sp.index, gen, piece: sp,
        }).then((alive) => { if (alive) landed(sp); return alive; });
        if (streamed) { seats.push(seat); continue; }
        if (!(await seat)) return;
      }
      await Promise.all(seats); // every piece seated (or the visit over: each seat answers for itself)
    } finally { settle(); }
  }

  /**
   * THE ROOM YOU STAND IN LOADS FIRST (THE QUEUE LAW, 2026-09-29, applied to furniture). A planned tower seats seven
   * times the pieces the home furnisher did, one awaited load after another, behind a city's whole streaming queue —
   * the first promoted perf run read 50 s to the first piece and 94 s to the last on a cold 8-floor apartment block
   * (SwiftShader). Every DISTINCT model the view seats is asked for at once on the asset gate's urgent lane (a
   * furniture GLB is a few hundred KB; the gate is last-in-first-out among urgent asks, so the storeys are asked top
   * down and the ground floor's models come first); `placeSlot` then awaits loads already in flight. The returned
   * closure releases the prefetch's own handles once the seating pass ends — every clone holds its own.
   */
  private prefetchFurniture(view: SeatView, here: number | null = null, urgent = true): () => void {
    const conn = atlas.conn, assets = this.assets;
    if (!conn || !assets || typeof assets.loadRepresentation !== 'function') return () => {};
    const lod = activePreset().settlementForceLowLod ? 1 : 0; // `loadForTier`'s own level: the same cache key
    let keys: string[];
    if (here === null) { // the lever (`atlas:interior-stream = 'off'`): the storeys top down, each key at its first sight
      keys = [];
      const asked = new Set<string>();
      for (let i = view.floors.length - 1; i >= 0; i--) for (const sp of view.floors[i].pieces) {
        if (!sp.shown || asked.has(sp.key) || isKitProp(sp.key)) continue;
        asked.add(sp.key); keys.push(sp.key);
      }
    } else {
      // (slice H) THE ORDER OF THE ASKS — `prefetchOrder`: the lanes free right now take the nearest models, the rest
      // queue farthest first so the last-in-first-out urgent lane serves the nearest of them next.
      const gate = assets as unknown as { gateActive?: number };
      const lanes = (assets.constructor as unknown as { LOAD_LANES?: number }).LOAD_LANES ?? 4;
      keys = prefetchOrder(view, here, urgent && typeof gate.gateActive === 'number' ? Math.max(0, lanes - gate.gateActive) : 0);
      if (!urgent) keys = prefetchOrder(view, here, Number.MAX_SAFE_INTEGER); // the ordinary lane is first-in first-out: nearest first
    }
    const handles: Array<Promise<{ release: () => void } | null>> = [];
    for (const key of keys) {
      const id = this.furnAssetOf(key);
      if (id === undefined) continue;
      handles.push(assets.loadRepresentation(id, lod, urgent ? { urgent: true } : undefined).then((h) => h, () => null));
    }
    return (): void => { for (const h of handles) void h.then((x) => x?.release()); };
  }

  /** The render asset of a furniture key — ONE map per change of the catalog table (slice H: the visit used to spread the whole
   *  `prefab_model` table once per piece: 22.7 ms on a 112-piece tower's door). */
  private furnAssets: { stamp: number; map: Map<string, bigint> } | null = null;
  private furnAssetOf(key: string): bigint | undefined {
    const conn = atlas.conn;
    if (!conn) return undefined;
    const n = tableStamp(conn.db.prefabModel); // NaN on an unpatched table (a bed, the kill switch): never equal, so the map is rebuilt — the old cost, never a stale id
    if (!this.furnAssets || this.furnAssets.stamp !== n) {
      const map = new Map<string, bigint>();
      try { for (const p of conn.db.prefabModel.iter()) if (p.prefabKey.startsWith('prop:furn_')) map.set(p.prefabKey.slice('prop:furn_'.length), p.assetId); } catch { return undefined; }
      this.furnAssets = { stamp: n, map };
    }
    return this.furnAssets.map.get(key);
  }

  // ─── THE DOOR PROMPT PREWARMS THE VISIT (slice H, 2026-10-02) ──────────────────────────────────────────────────────
  // MEASURED (impl-polish.md): a cold door is 89–98 % waiting for furniture masters and, on a small room, a 17–51 ms
  // synchronous plan on the frame after E. Both can start while the prompt stands on the door: the room is planned
  // into the plan cache (the visit's own `planRoom` then answers from it) and its masters are asked on the ORDINARY
  // lane, nearest storey first. Nothing is built, hidden or added to the scene; the visit is exactly the visit. The
  // handles are released a moment after the prompt leaves the door (or the visit's own asks have taken over).
  // Lever: `atlas:interior-prewarm = 'off'`.
  private prewarm: { id: string; since: number; started: boolean; settle: (() => void) | null } | null = null;
  private readonly prewarmOld: Array<{ until: number; settle: () => void }> = [];
  private prewarmFor: string | null = null;
  private prewarmLast = { planned: false, via: 'none' as string, planMs: 0, models: 0, ms: 0 };
  private prewarmCount = 0;

  /** The 6 Hz half: which door (if any) the prompt stands on now. */
  private prewarmDoor(id: string | null): void {
    const now = performance.now();
    for (let i = this.prewarmOld.length - 1; i >= 0; i--) if (now >= this.prewarmOld[i].until) { this.prewarmOld[i].settle(); this.prewarmOld.splice(i, 1); }
    const cur = this.prewarm;
    if (cur && cur.id !== id) {
      if (cur.settle) this.prewarmOld.push({ until: now + PREWARM_KEEP_MS, settle: cur.settle });
      this.prewarm = null;
    }
    if (!id || id === 'exit' || !interiorPrewarmEnabled()) return;
    const w = this.prewarm ?? (this.prewarm = { id, since: now, started: false, settle: null });
    if (w.started || now - w.since < PREWARM_DWELL_MS) return;
    w.started = true;
    void this.prewarmPass(w);
  }

  /** Plan the room behind a door and ask for its masters — what `furnishFor` will ask for, a moment early. Never throws. */
  private async prewarmPass(w: NonNullable<BuildingInteriorModule['prewarm']>): Promise<boolean> {
    const t0 = performance.now();
    try {
      const b = enterables.get(w.id);
      if (!b || !interiorPlannerEnabled() || !this.doorGate(b).ok) return false; // the lever plans nothing; a locked door loads nothing
      const spec = interiorSpec(b.w, b.h, b.d, b.key);
      const purpose = resolvePurpose(b.id, b.key);
      const packInterior = isPackInterior(b.id, b.key);
      const pin = packInterior ? this.pinOf(b) : null;
      const pack = packInterior ? this.deedPack(b) : null;
      const layout = layoutFor(b.id, b.key, pin);
      // the tradition the visit will resolve — the look WITHOUT its shell (no room is recorded for the audio runtime)
      const tradition: WiringTradition = styleFor(resolveLook(b, purpose.purpose)).tradition;
      const planned = await planRoom(planInputFor(b, purpose.purpose, layout, tradition), spec);
      this.prewarmFor = w.id; this.prewarmCount++;
      this.prewarmLast = { planned: true, via: planned.via, planMs: Math.round(planned.ms * 10) / 10, models: 0, ms: Math.round((performance.now() - t0) * 10) / 10 };
      if (this.prewarm !== w || this.active) return true; // the prompt moved on, or E was pressed: the visit asks for itself
      const descriptor = planned.result.descriptor;
      if (furnishRouteFor(b.id, b.key, true, descriptor) !== 'descriptor') return true; // a legacy room picks its pieces as it seats them
      const view = seatView(descriptor, { pack: packInterior ? pack : undefined, detail: clampDetail(activePreset().interiorDetail) as DetailLevel });
      const here = storeyAt(atlas.localY - b.groundY, spec.floors, FLOOR_H);
      this.prewarmLast.models = prefetchOrder(view, here, 0).length;
      w.settle = this.prefetchFurniture(view, here, false);
      return true;
    } catch (e) {
      console.warn(`[BuildingInterior] prewarm of ${w.id} failed — the visit plans and loads at the door as before`, e);
      return false;
    }
  }

  /** The layout pin of a deed (null = no row = layout 0). */
  private pinOf(b: EnterableBuilding): LayoutPin | null {
    if (!b.id.startsWith('deed:')) return null;
    const db = atlas.conn?.db as unknown as { structureLayout?: { structureId: { find(id: bigint): { layoutRevision: number; digest: string } | undefined } } } | undefined;
    try { const row = db?.structureLayout?.structureId.find(BigInt(b.id.slice(5))); return row ? { layoutRevision: row.layoutRevision, digest: row.digest } : null; } catch { return null; }
  }

  private subscribeLayout(): void {
    const conn = atlas.conn;
    if (!conn || this.layoutSubscribed) return;
    this.layoutSubscribed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM structure_layout', 'SELECT * FROM platform_flag']); } catch { /* pre-publish server */ }
    const t = (conn.db as unknown as Record<string, { onInsert?(cb: (c: unknown, r: { structureId: bigint }) => void): void; onUpdate?(cb: (c: unknown, o: unknown, r: { structureId: bigint }) => void): void; onDelete?(cb: (c: unknown, r: { structureId: bigint }) => void): void } | undefined>).structureLayout;
    const moved = (id: bigint): void => this.onPinMoved(id);
    t?.onInsert?.((_c, r) => moved(r.structureId));
    t?.onUpdate?.((_c, _o, r) => moved(r.structureId));
    t?.onDelete?.((_c, r) => moved(r.structureId));
  }

  /** A pin row moved: re-seat the room when the LAYOUT it plans at changed; a digest stamp only updates the record. */
  private onPinMoved(structureId: bigint): void {
    const a = this.active;
    if (!a || a.b.id !== `deed:${structureId}` || !isPackInterior(a.b.id, a.b.key)) return;
    const pin = this.pinOf(a.b);
    const layoutChanged = a.plannerOn ? layoutFor(a.b.id, a.b.key, pin) !== a.plan.layout : (!!pin && pin.layoutRevision >= 1) !== !a.plan.keyedOk;
    // A REFIT (the pin's digest cleared) may be stamped again this session — and a drifted room re-plans for it:
    // the Refit is what moves a pinned room onto today's plan and today's tradition.
    if (pin && pin.digest === '') this.stampAsked.delete(a.b.id);
    if (layoutChanged || (!!pin && pin.digest === '' && a.plan.digestDrift)) { void this.refurnishDeed(); return; }
    const d = a.plan.descriptor;
    // (drift is judged against the DRAWN plan here: a stamp that lands is this room's own digest)
    a.plan = { ...a.plan, pin, digestDrift: !!pin && !!d && pin.layoutRevision >= 1 && pin.digest !== '' && pin.digest !== d.digest };
    this.publishActive(a);
    if (pin && pin.digest === '') this.stampDigest(a);
  }

  /** The owner's client writes a fresh pin's digest ONCE (the server stamps the revision; it has no planner). */
  private stampDigest(session: NonNullable<BuildingInteriorModule['active']>): void {
    const { b, plan } = session;
    const conn = atlas.conn;
    if (!conn?.identity || !plan.pin || plan.pin.layoutRevision < 1 || plan.pin.digest !== '' || !plan.descriptor || plan.route !== 'descriptor') return;
    if (plan.pin.layoutRevision !== plan.layout || !DIGEST_RE.test(plan.descriptor.digest) || this.stampAsked.has(b.id)) return;
    const id = BigInt(b.id.slice(5));
    const db = conn.db as unknown as {
      structure: { structureId: { find(i: bigint): { owner: { isEqual(o: unknown): boolean } } | undefined } };
      platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } };
    };
    try {
      if (!db.structure.structureId.find(id)?.owner.isEqual(conn.identity)) return; // the owner's to write
      if (db.platformFlag?.key.find(LAYOUT_PIN_FLAG)?.enabled === false) return;    // the pin is switched off on this shard
    } catch { return; }
    this.stampAsked.add(b.id);
    const r = conn.reducers as unknown as { stampStructureLayout?(a: { structureId: bigint; digest: string }): Promise<void> };
    r.stampStructureLayout?.({ structureId: id, digest: plan.descriptor.digest }).catch(() => { this.stampAsked.delete(b.id); });
  }

  /** Publish what the room IS for the resident mirror, the E arbiter and the console (`interior_wiring.activeInterior`). */
  private publishActive(session: NonNullable<BuildingInteriorModule['active']>): void {
    if (this.active !== session) return;
    const p = session.plan;
    setActiveInterior({
      id: session.b.id, version: ++session.version, planner: session.plannerOn, route: p.route, layout: p.layout, pin: p.pin, pack: p.pack,
      tradition: p.tradition, plannedTradition: p.plannedTradition,
      descriptor: p.descriptor, report: p.report, view: p.view, room: p.room, keyedOk: p.keyedOk, digestDrift: p.digestDrift, planVia: p.via, planMs: p.ms,
    });
  }

  private plannerReport(): Record<string, unknown> {
    const a = this.active;
    const base = { lever: interiorPlannerEnabled(), cache: planCacheStats(), client: planClientStats() };
    if (!a) return { ...base, inside: null };
    const p = a.plan, d = p.descriptor;
    return {
      ...base, inside: a.b.id, key: a.b.key, plannerOn: a.plannerOn, route: p.route, layout: p.layout, pin: p.pin, pack: p.pack, keyedOk: p.keyedOk, digestDrift: p.digestDrift,
      via: p.via, ms: p.ms, version: a.version,
      digest: d?.digest ?? null, status: d?.status ?? null, rung: d?.rung ?? null, archetype: d?.recipe.archetypeId ?? null, algorithm: d?.recipe.algorithm ?? null,
      tradition: d?.recipe.tradition ?? null, reasons: d?.reasons ?? [], hard: p.report?.hard ?? [],
      floors: d?.floors.map((f) => ({ floor: f.floor, program: f.program, source: f.source, pieces: f.pieces.length, anchors: f.anchors.length, zones: f.zones.length, units: f.units.length })) ?? [],
      links: d?.links.length ?? 0, lift: d?.liftCandidate ? { wall: d.liftCandidate.wall, calls: d.liftCandidate.calls.length } : null,
      view: p.view ? { pieces: p.view.pieces, shown: p.view.shown, hidden: p.view.hidden, undrawn: p.view.undrawn, colliders: p.view.colliders, storageKeys: [...p.view.storageKeys] } : null,
      props: a.group.children.filter((o) => o.userData.kitProp).length, fixtures: (a.fixtures?.userData.fixtureCount as number | undefined) ?? 0,
      fixtureModels: (a.fixtures?.userData.fixtureModels as number | undefined) ?? 0,
      room: p.room ? { sockets: p.room.sockets.length, stands: p.room.stands.length, rugs: p.room.rugs.length } : null,
      seated: a.group.children.filter((o) => o.userData.furnKey).length, furnitureColliders: a.furnCids.length, shellColliders: a.cids.length,
      // (slice G) the rooms: the plan's walls, doorways and rooms, their boxes, and what the server made of this client's reports
      partitions: (a.partitions?.userData.partitionCount as number | undefined) ?? 0, doorways: (a.partitions?.userData.doorwayCount as number | undefined) ?? 0,
      rooms: p.descriptor ? p.descriptor.floors.reduce((k, f) => k + f.rooms.length, 0) : 0, partitionColliders: a.partCids.length,
      presence: this.presence ? { room: this.presence.key, accepted: this.presence.accepted, sent: this.presence.sent, ok: this.presence.ok, refused: this.presence.refused, lastWhy: this.presence.lastWhy } : null,
      // (task 2) THE TENANT'S DOOR: leaves hung, shut for this viewer, boxes standing
      unitDoors: { leaves: a.doors.items.length, shut: a.doors.items.filter((q) => q.shut).length, solid: a.doors.items.filter((q) => q.solid).length },
      // C2: crafted pieces' boxes, the last-resort ground, the rescue count, the look's tradition vs the pinned one
      itemColliders: [...a.itemCids.entries()].map(([id, v]) => ({ id, cid: v.cid, floor: v.floor })), safety: a.safety, rescues: this.rescues, lastRescue: this.lastRescue,
      lookTradition: p.tradition, plannedTradition: p.plannedTradition,
      liveStorage: p.view ? p.view.floors.flatMap((f) => f.pieces).filter((sp) => sp.storageLive && sp.shown).map((sp) => sp.storageKey) : [],
    };
  }

  /** Every furniture clone the visit seated, with its tags (probes read this; never the scene by hand). */
  private pieceReport(): Array<Record<string, unknown>> {
    const a = this.active;
    if (!a) return [];
    const c = Math.cos(a.b.yaw), sn = Math.sin(a.b.yaw);
    return a.group.children.filter((o) => o.userData.furnKey).map((o) => {
      const dx = o.position.x - a.b.x, dz = o.position.z - a.b.z;
      return {
        key: o.userData.furnKey, floor: o.userData.furnFloor, index: o.userData.furnIndex, slot: o.userData.furnSlot ?? null, storageKey: o.userData.furnStorageKey ?? null,
        tier: o.userData.furnTier ?? null, pack: !!o.userData.packFurniture, collider: o.userData.furnCollider ?? null,
        lx: Math.round((c * dx - sn * dz) * 1000) / 1000, lz: Math.round((sn * dx + c * dz) * 1000) / 1000, y: Math.round((o.position.y - a.b.groundY) * 1000) / 1000,
        // the PLANNED yaw (a mirrored left drape is drawn half a turn back, mirrored — slice H)
        yaw: Math.round((o.rotation.y - a.b.yaw - (o.userData.mirroredDrape ? Math.PI : 0)) * 10000) / 10000, mirrored: !!o.userData.mirroredDrape,
      };
    });
  }

  // ─── The debug overlay (dev-gated) ────────────────────────────────────────────────────────────────────────────────

  private setDebug(on: boolean): unknown {
    if (!interiorDebugAllowed()) return false;
    this.debugOn = on;
    if (!on) { this.debugHandle?.dispose(); this.debugHandle = null; this.debugVersion = -1; return true; }
    this.debugVersion = -1;
    this.debugTick();
    return this.debugHandle ? { ...this.debugHandle.counts, digest: this.debugHandle.view.digest, candidates: this.debugHandle.view.candidates.length } : false;
  }
  private debugTick(): void {
    const a = this.active;
    if (!a || !a.plan.descriptor) { if (this.debugHandle) { this.debugHandle.dispose(); this.debugHandle = null; this.debugVersion = -1; } return; }
    if (this.debugHandle && this.debugVersion === a.version) return;
    this.debugHandle?.dispose();
    const p = a.plan;
    this.debugHandle = buildInteriorDebug(p.descriptor!, p.report, a.spec, { x: a.b.x, z: a.b.z, yaw: a.b.yaw, groundY: a.b.groundY }, {
      title: `${a.b.id} · ${a.b.key}`,
      lines: [
        `route   ${p.route} · layout ${p.layout}${p.pin ? ` · pin ${p.pin.layoutRevision}/${p.pin.digest ? p.pin.digest.slice(0, 12) : 'no digest'}` : ' · no pin'}${p.digestDrift ? ' · DIGEST DRIFT' : ''}${p.pack ? ` · pack ${p.pack}` : ''}`,
        `plan    ${p.via} ${p.ms} ms${p.view ? ` · seated ${p.view.shown} of ${p.view.pieces} (${p.view.hidden} hidden by detail) · ${p.view.colliders} colliders` : ''}`,
      ],
    });
    this.scene.add(this.debugHandle.group);
    this.debugVersion = a.version;
  }

  /** Deterministic furnished layout for a versona home: a few pieces from the
   *  shared furniture slate along the walls + center of each lower floor. */
  private async furnishVersonaHome(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const conn = atlas.conn;
    const assets = this.assets;
    if (!conn || !assets) return;
    // SESSION IDENTITY, not building id: exit + re-enter the same home while
    // this pass awaits a load made the old pass furnish the dead group and
    // push its releases onto the new session (the home stayed bare).
    const session = this.active;
    if (!session) return;
    // Seed from the building id — stable per home, varied across the street.
    let h = 0x811c9dc5;
    for (let i = 0; i < b.id.length; i++) { h ^= b.id.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    const rnd = (): number => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 1000) / 1000; };
    // ── Layout by interior-design rules (web research 2026-08-14): keep the
    // primary walkways clear (≥0.9 m door→stairs corridor), anchor pieces on
    // walls FACING the room (sofa perpendicular to circulation), keep the
    // conversation grouping ~1.4 m (sofa→table), never block the doorway or
    // the stair approach. Floors are THEMED: living below, sleeping above,
    // study/den on the third — a believable home, not scattered props. ──
    const hwI = spec.hw, hdI = spec.hd;
    const st = spec.stair;
    const clearOf = (x: number, z: number): boolean => {
      // Door corridor: from the -Z door straight in.
      if (Math.abs(x) < 1.4 && z < -hdI + 3.4) return false;
      // Stair zone + its approach.
      if (x > st.x - 2.0 && x < st.x + 2.0 && z > st.z - st.d / 2 - 1.8 && z < st.z + st.d / 2 + 1.8) return false;
      // Inside the room bounds with wall clearance.
      return Math.abs(x) < hwI - 0.55 && Math.abs(z) < hdI - 0.55;
    };
    const pickCat = (cat: FurnitureSpec['cat']): string => {
      const pool = Object.values(FURNITURE).filter((s) => s.cat === cat);
      return pool[Math.floor(rnd() * pool.length) % pool.length].key;
    };
    // Per-floor themed slot plans (local coords; yaw faces the room).
    type Slot = { key: string; x: number; z: number; yaw: number };
    const planFor = (f: number): Slot[] => {
      const s: Slot[] = [];
      if (f === 0) { // living: sofa on the +X wall facing in, table 1.4 m out
        s.push({ key: pickCat('seating'), x: hwI - 1.0, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: 'table_low', x: hwI - 2.45, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: 'rug_woven', x: hwI - 2.4, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: pickCat('utility'), x: 1.8, z: hdI - 0.95, yaw: Math.PI });
        s.push({ key: pickCat('light'), x: hwI - 0.8, z: hdI - 0.8, yaw: Math.PI * 0.75 });
        s.push({ key: pickCat('decor'), x: 2.3, z: -hdI + 1.0, yaw: 0 });
      } else if (f === 1) { // sleeping: bed on the +Z wall, storage opposite
        s.push({ key: rnd() < 0.5 ? 'bed_double' : 'bed_single', x: 1.7, z: hdI - 1.35, yaw: Math.PI });
        s.push({ key: pickCat('storage'), x: hwI - 0.85, z: 0.4, yaw: -Math.PI / 2 });
        s.push({ key: 'chest_storage', x: 1.7, z: hdI - 3.3, yaw: Math.PI });
        s.push({ key: 'lamp_table', x: hwI - 0.75, z: hdI - 0.85, yaw: Math.PI * 0.75 });
        s.push({ key: 'rug_woven', x: 1.7, z: hdI * 0.25, yaw: 0 });
      } else { // den/study: desk on the +Z wall, shelves, greenery
        s.push({ key: rnd() < 0.5 ? 'desk_writing' : 'desk_terminal', x: 1.8, z: hdI - 1.0, yaw: Math.PI });
        s.push({ key: 'chair_dining', x: 1.8, z: hdI - 2.1, yaw: 0 });
        s.push({ key: pickCat('storage'), x: hwI - 0.85, z: -0.4, yaw: -Math.PI / 2 });
        s.push({ key: pickCat('decor'), x: hwI - 0.9, z: hdI - 0.9, yaw: Math.PI * 0.75 });
        s.push({ key: pickCat('light'), x: 2.4, z: -hdI + 1.1, yaw: 0 });
      }
      return s.filter((sl) => clearOf(sl.x, sl.z));
    };
    const floors = Math.min(spec.floors, 3); // furnish the lived-in lower floors
    for (let f = 0; f < floors; f++) {
      const plan = planFor(f);
      for (const slot of plan) {
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * 3.4, (rnd() - 0.5) * 0.12);
        if (!alive) return;
      }
    }
  }

  /** Purpose furnishing (THE PURPOSE LAW): the pure plan lib picks the pieces
   *  per floor from the resolved purpose; this only seats them. */
  private async furnishByPurpose(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number }, purpose: InteriorPurpose,
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const frame = { hw: spec.hw, hd: spec.hd, stair: spec.stair };
    const floors = Math.min(spec.floors, 3); // the lived-in lower floors, like the versona homes
    for (let f = 0; f < floors; f++) {
      for (const slot of purposePlan(purpose, frame, f)) {
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * FLOOR_H);
        if (!alive) return;
      }
    }
  }

  /** THE FURNISH PACKS (2026-09-23): a player structure's interior is EMPTY until its owner buys a pack from the
   *  console; the pack seats its share of the purpose plan (lib/furnish_packs.ts). Pieces are tagged and their
   *  releases kept apart, so a pack change re-seats them without leaving the room. */
  private async furnishDeed(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number }, purpose: InteriorPurpose,
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const pack = this.deedPack(b);
    if (!pack) return; // unfurnished — the empty shell
    const gen = session.packGen;
    const frame = { hw: spec.hw, hd: spec.hd, stair: spec.stair };
    const plan = packPlan(pack, purpose, frame, spec.floors);
    for (let f = 0; f < plan.length; f++) {
      for (const slot of plan[f]) {
        if (session.packGen !== gen) return; // a newer pack took over
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * FLOOR_H, 0, { sink: session.packSink, tag: 'packFurniture', floor: f, index: plan[f].indexOf(slot), gen });
        if (!alive) return;
      }
    }
  }

  /** The pack a deed's owner bought (null = none). */
  private deedPack(b: EnterableBuilding): FurnishPack | null {
    if (!b.id.startsWith('deed:')) return null;
    const db = atlas.conn?.db as unknown as { structureFurnish?: { structureId: { find(id: bigint): { pack: number } | undefined } } } | undefined;
    try { const row = db?.structureFurnish?.structureId.find(BigInt(b.id.slice(5))); return row ? furnishPack(row.pack) : null; } catch { return null; }
  }


  // ─── Slice F's procedural props and shell fixtures (lib/interior_kit_props.ts) ─────────────────────────────────────

  /** What a prop's box wears: the shell's five, or the look's lazy `partition` / `lift` group — absent under the look's
   *  lever, where the role falls back to a shell material. */
  private roleMat(session: NonNullable<BuildingInteriorModule['active']>, role: PropRole): THREE.Material {
    const r = PROP_ROLE[role];
    const mats = session.mats as unknown as Record<string, THREE.Material | Record<string, THREE.Material> | undefined>;
    let m: THREE.Material | undefined;
    if (r.group === 'shell') m = mats[r.key] as THREE.Material | undefined;
    else { try { m = (mats[r.group] as Record<string, THREE.Material> | undefined)?.[r.key]; } catch { m = undefined; } }
    m ??= mats[r.fallback] as THREE.Material;
    session.propMats.add(m);
    return m;
  }
  /** A prop as its boxes — the spec's origin is its base centre, +Z its front (the slate's convention). */
  private propObject(session: NonNullable<BuildingInteriorModule['active']>, spec: KitPropSpec): THREE.Group {
    const g = new THREE.Group();
    g.name = `prop:${spec.key}`;
    for (const bx of spec.boxes) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(bx.w, bx.h, bx.d), this.roleMat(session, bx.role));
      mesh.position.set(bx.x, bx.y, bx.z);
      mesh.rotation.set(0, bx.yaw ?? 0, bx.roll ?? 0, 'YXZ');
      g.add(mesh);
    }
    return g;
  }
  /** Seat one PLANNER piece that is a procedural prop (a folding screen, a riser, a rail, a counter front): built, not
   *  loaded, at exactly its size; tagged like any seated piece; solid where it blocks walking, a FLOOR where a body
   *  walks on it. One mesh per material (a tower of screens is not a thousand draws). */
  private placeProp(
    session: NonNullable<BuildingInteriorModule['active']>, toWorld: (lx: number, lz: number) => { x: number; z: number }, sp: SeatPiece, pack: boolean,
  ): void {
    const spec = kitProp(sp.key);
    if (!spec || this.active !== session) return;
    const tProp = performance.now();
    const { b, group } = session;
    const g = this.propObject(session, spec);
    const wp = toWorld(sp.x, sp.z);
    g.position.set(wp.x, b.groundY + sp.y, wp.z);
    g.rotation.y = b.yaw + sp.yaw;
    mergeStaticMeshes(g);
    g.userData.furnKey = sp.key; g.userData.furnFloor = sp.floor; g.userData.furnIndex = sp.index;
    g.userData.furnSlot = sp.slot; g.userData.furnTier = sp.tier; g.userData.kitProp = true;
    if (sp.storageKey) g.userData.furnStorageKey = sp.storageKey; // a pack piece's key, like any clone's (a prop has no function, so nothing ever opens under it)
    g.userData.furnStorageLive = sp.storageLive;
    if (pack) {
      g.userData.packFurniture = true;
      // a pack's props leave with the pack: their geometry is ours (the materials are the shell's — freed with the visit)
      session.packSink.push(() => g.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); }));
    }
    group.add(g);
    const cb = propColliderBox({ x: sp.x, z: sp.z, yaw: sp.yaw, y: sp.y }, sp.key, sp.collide);
    if (cb && sp.source === 'planned') {
      const cw = toWorld(cb.lx, cb.lz);
      const cid = `int:${b.id}:furn:${sp.floor}:${session.furnSeq++}`;
      if (this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [cb.hx, cb.hy, cb.hz] }, { x: cw.x, y: b.groundY + cb.ly, z: cw.z, yaw: b.yaw + cb.yaw, scale: 1 })) {
        session.furnCids.push(cid);
        g.userData.furnCollider = cid;
        if (cb.kind === 'floor') g.userData.furnWalkOn = true;
        if (this.perf && this.perf.id === b.id && this.perf.ms.furnished === null) this.perf.furnitureColliders++;
      }
    }
    this.perfCost('props', tProp);
    this.perfPiece(session, sp.y);
  }
  /**
   * THE PARTITIONS (slice G, 2026-10-01): the plan's walls, built from the descriptor — the solid spans as THICK boxes
   * (drawn and collided at the partition's 0.4 m: interiors.md laws 6–8, a wall a body touches from both sides keeps
   * its mid-plane out of reach), each doorway an OPEN aperture with a header and a frame (no leaf, nothing animated).
   * D's `shellMaterials(look).partition` where the look has it, the shell's wall / trim under the look's lever. One
   * mesh per material; hidden and restored with the visit; rebuilt when the room is re-planned; counted in
   * `__interior.perf()`. THE LEVER `atlas:interior-planner = 'off'` plans nothing, so it builds none.
   */
  private placePartitions(
    session: NonNullable<BuildingInteriorModule['active']>, toWorld: (lx: number, lz: number) => { x: number; z: number }, d: InteriorDescriptor,
  ): void {
    this.dropPartitions(session);
    if (this.active !== session) return;
    const boxes = partitionBoxesFor(d);
    if (boxes.length === 0) return;
    const { b } = session;
    const mats = session.mats as unknown as Record<string, THREE.Material | Record<string, THREE.Material> | undefined>;
    const matOf = (role: 'panel' | 'frame'): THREE.Material => {
      let m: THREE.Material | undefined;
      try { m = (mats.partition as Record<string, THREE.Material> | undefined)?.[role]; } catch { m = undefined; }
      m ??= mats[role === 'panel' ? 'wall' : 'trim'] as THREE.Material;
      session.propMats.add(m);
      return m;
    };
    const root = new THREE.Group();
    root.name = 'interior-partitions';
    root.userData.interiorId = b.id;
    root.position.set(b.x, b.groundY, b.z); // a scene child is ABSOLUTE; the walls are building-local under it
    root.rotation.y = b.yaw;
    let n = 0;
    for (const bx of boxes) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(bx.w, bx.h, bx.d), matOf(bx.role));
      mesh.position.set(bx.x, bx.y, bx.z);
      root.add(mesh);
      if (!bx.collide) continue;
      const cw = toWorld(bx.x, bx.z);
      const cid = `int:${b.id}:part:${bx.floor}:${n++}`;
      if (this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [bx.w / 2, bx.h / 2, bx.d / 2] }, { x: cw.x, y: b.groundY + bx.y, z: cw.z, yaw: b.yaw, scale: 1 })) session.partCids.push(cid);
    }
    mergeStaticMeshes(root);
    root.traverse((o) => { o.layers.enable(RAIN_SHELTER_LAYER); });
    root.userData.partitionCount = d.floors.reduce((k, f) => k + f.partitions.length, 0);
    root.userData.doorwayCount = d.floors.reduce((k, f) => k + f.doorways.length, 0);
    root.userData.roomCount = d.floors.reduce((k, f) => k + f.rooms.length, 0);
    this.scene.add(root);
    session.partitions = root;
  }
  private dropPartitions(session: NonNullable<BuildingInteriorModule['active']>): void {
    this.dropUnitDoors(session); // (task 2) a door hangs in a wall: no walls, no doors — the next tick rebuilds them
    for (const cid of session.partCids) this.physics.removeStaticCollider(cid);
    session.partCids.length = 0;
    const root = session.partitions;
    if (!root) return;
    session.partitions = null;
    this.scene.remove(root);
    root.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); });
  }

  // ─── THE TENANT'S DOOR (slice G task 2, 2026-10-02) ──────────────────────────────────────────────────────────────

  /** The two levers the door rule hangs on (`interior_rooms_v1`, `interior_rentals_v1` — ABSENT = ON), read from the
   *  rows this module already subscribes; twice a second. */
  private doorLevers(): { rooms: boolean; rentals: boolean } {
    const now = performance.now();
    if (now - this.doorLeversAt < 500) return this.doorLeversVal;
    this.doorLeversAt = now;
    const db = atlas.conn?.db as unknown as { platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } } } | undefined;
    let rooms = true, rentals = true;
    try {
      rooms = db?.platformFlag?.key.find(ROOMS_FLAG)?.enabled !== false;
      rentals = db?.platformFlag?.key.find(RENTALS_FLAG)?.enabled !== false;
    } catch { /* the table mid-churn: absent = on */ }
    if (rooms !== this.doorLeversVal.rooms || rentals !== this.doorLeversVal.rentals) this.doorLeversVal = { rooms, rentals };
    return this.doorLeversVal;
  }

  /**
   * THE TENANT'S DOOR, every frame of a visit. The server refuses every ENTRY of a let unit's room but its tenant's
   * (`enterInteriorRoom` → lib/interior_door_ops.ts); here the visit hangs a LEAF in each let unit's doorway and
   * stands a THICK box in the aperture exactly where the server would refuse:
   *   · the leaves are rebuilt when what they are built from moves — the plan (its digest), the LEASE ROWS (the let
   *     store's version: a lease signed while you stand in the corridor shuts the door in front of you, one that ends
   *     opens it), who you are, either lever;
   *   · THE ONE-WAY RULE (lib/interior_doors.ts): a shut door's box stands only for a body OUTSIDE the room. A body
   *     inside it when the lease is signed, or standing in the doorway, has no box put on it or in its way: it walks
   *     out, the leaf shuts behind it, and it cannot come back. NOBODY IS EVER SEALED IN;
   *   · the tenant, an unlet unit, either lever off: no box. The tenant's own leaf is drawn slid open.
   * A legacy room, the planner's lever and a room below the rooms revision have no unit rooms, so no doors.
   */
  private doorTick(): void {
    const a = this.active;
    if (!a) return;
    const d = a.partitions ? a.plan.descriptor : null; // no walls (the lever, a legacy room, a re-plan in flight) → no doors
    const me = atlas.conn?.identity?.toHexString() ?? '';
    const lv = this.doorLevers();
    const sig = d ? `${d.digest}|${letStoreVersion()}|${me}|${lv.rooms ? 1 : 0}${lv.rentals ? 1 : 0}` : '';
    if (sig !== a.doors.sig) this.buildUnitDoors(a, d, sig, me, lv);
    const items = a.doors.items;
    if (items.length === 0) return;
    const { b } = a;
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const dx = atlas.localX - b.x, dz = atlas.localZ - b.z;
    const lx = dx * cos - dz * sin, lz = dx * sin + dz * cos;
    const at = visitRoomAt(d, a.spec.floors, lx, atlas.localY - b.groundY, lz);
    const body = { floor: at.floor, lx, lz, roomId: at.roomId };
    for (const it of items) {
      let solid = false;
      if (it.shut) {
        it.pass = doorPassNext(it.pass, doorRelation(it.door, body));
        solid = doorBlocks(true, it.pass);
      }
      if (solid !== it.solid) {
        if (solid) {
          const bx = doorBox(it.door);
          solid = this.physics.addStaticCollider(it.cid, { shape: 'box', halfExtents: [bx.w / 2, bx.h / 2, bx.d / 2] },
            { x: b.x + bx.x * cos + bx.z * sin, y: b.groundY + bx.y, z: b.z - bx.x * sin + bx.z * cos, yaw: b.yaw, scale: 1 });
        } else this.physics.removeStaticCollider(it.cid);
        it.solid = solid;
      }
      // the leaf is drawn SHUT exactly while its box stands — a body the rule lets out sees its way out
      if (it.shutLeaf.visible !== solid) { it.shutLeaf.visible = solid; it.openLeaf.visible = !solid; }
    }
  }

  /** (Re)build the leaves: one pair (shut / slid open) per doorway of a LET unit, the look's trim for the slab and its
   *  partition panel for the insets. A door that stays shut across a rebuild KEEPS its latch and its box (a lease
   *  signed elsewhere in the building must not open the door a body is leaning on). */
  private buildUnitDoors(
    session: NonNullable<BuildingInteriorModule['active']>, d: InteriorDescriptor | null, sig: string, me: string, lv: { rooms: boolean; rentals: boolean },
  ): void {
    const old = new Map(session.doors.items.map((it) => [it.cid, it]));
    const oldRoot = session.doors.root;
    session.doors = { sig, root: null, items: [] };
    const { b } = session;
    const lets = d && lv.rooms && lv.rentals ? letUnitsOf(b.id) : [];
    const doors = lets.length > 0 ? unitDoorsOf(d) : [];
    const mats = session.mats as unknown as Record<string, THREE.Material | Record<string, THREE.Material> | undefined>;
    let pullMat: THREE.Material | null = null;
    const matOf = (role: 'panel' | 'frame' | 'door' | 'pull'): THREE.Material => {
      // (slice H) THE LEAF'S LOOK: the pull wears the trim's cap colour — the hand's metal — unlit like the room
      if (role === 'pull') {
        if (!pullMat) { pullMat = new THREE.MeshBasicMaterial({ color: styleFor(session.look).trim.cap }); pullMat.name = 'interior-door-pull'; session.propMats.add(pullMat); }
        return pullMat;
      }
      let m: THREE.Material | undefined;
      try { m = (mats.partition as Record<string, THREE.Material> | undefined)?.[role]; } catch { m = undefined; }
      m ??= mats[role === 'panel' || role === 'door' ? 'wall' : 'trim'] as THREE.Material;
      session.propMats.add(m);
      return m;
    };
    let root: THREE.Group | null = null;
    doors.forEach((door, n) => {
      const tenant = doorTenant(door, lets);
      if (tenant === '') return; // an unlet unit: an open doorway, as before
      if (!root) {
        root = new THREE.Group();
        root.name = 'interior-unit-doors';
        root.userData.interiorId = b.id;
        root.position.set(b.x, b.groundY, b.z); // a scene child is ABSOLUTE; the leaves are building-local under it
        root.rotation.y = b.yaw;
      }
      const leaf = (shut: boolean): THREE.Group => {
        const g = new THREE.Group();
        g.name = shut ? 'unit-door-shut' : 'unit-door-open';
        g.userData.unitId = door.unitId;
        for (const q of leafBoxes(door, shut)) {
          const mesh = new THREE.Mesh(new THREE.BoxGeometry(q.w, q.h, q.d), matOf(q.role));
          mesh.position.set(q.x, q.y, q.z);
          g.add(mesh);
        }
        mergeStaticMeshes(g);
        root!.add(g);
        return g;
      };
      const cid = `int:${b.id}:door:${door.floor}:${n}`;
      const shut = doorShut(door, lets, me, lv);
      const was = old.get(cid);
      const keep = !!was && was.shut && shut; // still shut: the latch and the box carry over
      const it: UnitDoorState = { door, tenant, shut, pass: keep ? was.pass : null, cid, solid: keep ? was.solid : false, shutLeaf: leaf(true), openLeaf: leaf(false) };
      it.shutLeaf.visible = it.solid; it.openLeaf.visible = !it.solid;
      if (keep) old.delete(cid);
      session.doors.items.push(it);
    });
    for (const it of old.values()) if (it.solid) this.physics.removeStaticCollider(it.cid);
    if (oldRoot) {
      this.scene.remove(oldRoot);
      oldRoot.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); });
    }
    if (root) { this.scene.add(root); session.doors.root = root; }
  }
  private dropUnitDoors(session: NonNullable<BuildingInteriorModule['active']>): void {
    for (const it of session.doors.items) if (it.solid) this.physics.removeStaticCollider(it.cid);
    const root = session.doors.root;
    session.doors = { sig: '', root: null, items: [] };
    if (!root) return;
    this.scene.remove(root);
    root.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); });
  }
  /** `__interior.doors()`: every let unit's door of the visit — where it hangs (building-local and absolute), whose
   *  room it is, whether it is shut for THIS viewer, the one-way latch and whether its box stands. */
  private doorReport(): Array<Record<string, unknown>> {
    const a = this.active;
    if (!a) return [];
    const cos = Math.cos(a.b.yaw), sin = Math.sin(a.b.yaw);
    return a.doors.items.map((it) => ({
      id: it.door.id, unitId: it.door.unitId, number: letUnitNumber(it.door.unitId), roomId: it.door.roomId, other: it.door.other, floor: it.door.floor,
      lx: it.door.x, lz: it.door.z, axis: it.door.axis, inSign: it.door.inSign, span: it.door.span, thickness: it.door.thickness, baseY: it.door.baseY,
      x: a.b.x + it.door.x * cos + it.door.z * sin, y: a.b.groundY + it.door.baseY, z: a.b.z - it.door.x * sin + it.door.z * cos,
      tenant: it.tenant.slice(0, 12), mine: it.tenant !== '' && it.tenant === (atlas.conn?.identity?.toHexString() ?? ''),
      shut: it.shut, pass: it.pass, solid: it.solid, leaf: it.shutLeaf.visible ? 'shut' : 'open', cid: it.cid,
    }));
  }

  /**
   * WHO IS WHERE (slice G): tell the server which room this client stands in — ONE report per room change, once the
   * room has held PRESENCE_DEBOUNCE_MS. The server judges it (bounded trust: the footprint, the room graph's doorways,
   * the stairs' walk time) and a refusal costs nothing: the last ACCEPTED room stands and the report is tried again
   * after PRESENCE_RETRY_MS while the room still differs. `leaveInterior` on the way out (`exit`).
   */
  private presenceTick(): void {
    const a = this.active;
    const conn = atlas.conn;
    if (!a || !conn || !presenceReportable(a.b.id)) return;
    const reducers = conn.reducers as unknown as { enterInteriorRoom?: (arg: { buildingId: string; floor: number; roomId: string; digest: string }) => Promise<void> };
    if (!reducers.enterInteriorRoom) return; // bindings older than the table: nothing to report to
    const cos = Math.cos(a.b.yaw), sin = Math.sin(a.b.yaw);
    const dx = atlas.localX - a.b.x, dz = atlas.localZ - a.b.z;
    const lx = dx * cos - dz * sin, lz = dx * sin + dz * cos;
    // THE THRESHOLD GATE (slice G, the world leg): a visit can be live while the body still stands OUTSIDE the shell
    // (the door prompt reaches 4.5 m; a dev-hook enter leaves it where it stood) and the server refuses "not at that
    // building" there — one refused reducer every retry for as long as the player lingers at the door. Report only
    // from inside the core (+0.5 m: the wall's own thickness), which is inside the server's footprint + pad.
    if (Math.abs(lx) > a.spec.hw + 0.5 || Math.abs(lz) > a.spec.hd + 0.5) return;
    const at = visitRoomAt(a.plan.descriptor, a.spec.floors, lx, atlas.localY - a.b.groundY, lz);
    const key = `${at.floor}|${at.roomId}`;
    const now = performance.now();
    let p = this.presence;
    if (!p || p.id !== a.b.id) p = this.presence = { id: a.b.id, key: '', since: now, accepted: '', busy: false, retryAt: 0, sent: 0, ok: 0, refused: 0, lastWhy: '' };
    if (key !== p.key) { p.key = key; p.since = now; }
    if (p.busy || key === p.accepted || now - p.since < PRESENCE_DEBOUNCE_MS || now < p.retryAt) return;
    p.busy = true; p.sent++;
    const st = p;
    void Promise.resolve().then(() => reducers.enterInteriorRoom!({ buildingId: a.b.id, floor: at.floor, roomId: at.roomId, digest: at.digest }))
      .then(() => { st.accepted = key; st.ok++; st.lastWhy = ''; }, (e: unknown) => {
        st.refused++; st.lastWhy = String((e as { message?: string } | null)?.message ?? e);
        // THE TENANT'S DOOR (task 2): the server's own words when it refuses a let room — shown once per 8 s, and the
        // report is tried again after DOOR_RETRY_MS, not every 2 s: a body that is in a let room anyway (it stood in
        // the doorway at the signing; a hard snap) is refused until it walks out or the lease ends
        const door = /Room \S+ is let[^\n]*/.exec(st.lastWhy);
        st.retryAt = performance.now() + (door ? DOOR_RETRY_MS : PRESENCE_RETRY_MS);
        if (door && performance.now() - this.doorToastAt > 8000) { this.doorToastAt = performance.now(); bus.emit('atlas:toast', { text: door[0], kind: 'error' }); }
      })
      .finally(() => { st.busy = false; });
  }
  private presenceLeave(): void {
    const p = this.presence;
    this.presence = null;
    if (!p || p.sent === 0) return;
    const reducers = atlas.conn?.reducers as unknown as { leaveInterior?: (arg: Record<string, never>) => Promise<void> } | undefined;
    try { void reducers?.leaveInterior?.({})?.catch(() => { /* gone already */ }); } catch { /* disconnected */ }
  }

  /** THE SHELL'S FIXTURES (slice F's `shellFixtures`): a unit's door frame and number plate at its threshold, the
   *  lift's door and call panel at the candidate shaft, ceiling light panels and door sconces where the room is that
   *  kind of room. They follow the PLAN (never a legacy descriptor — today's room is not dressed), collide with
   *  nothing, are in no digest, and are rebuilt whenever the room is re-planned. Detail 0 draws the bare shell. */
  private placeFixtures(
    session: NonNullable<BuildingInteriorModule['active']>, toWorld: (lx: number, lz: number) => { x: number; z: number },
    d: InteriorDescriptor, purpose: InteriorPurpose, detail: DetailLevel,
  ): void {
    this.dropFixtures(session);
    if (this.active !== session || !fixturesShown(d, detail)) return;
    const { b, spec } = session;
    const list = shellFixtures({ hw: spec.hw, hd: spec.hd, floors: spec.floors, stair: spec.stair }, d, fixtureOptionsFor(purpose));
    if (list.length === 0) return;
    const root = new THREE.Group();
    root.name = 'interior-fixtures';
    root.position.set(b.x, b.groundY, b.z); // a scene child is ABSOLUTE; the fixtures are building-local under it
    root.rotation.y = b.yaw;
    const plates: Array<() => void> = [];
    // one sub-group per storey and band (what stands on the floor / what hangs from the ceiling), merged per material:
    // a tower's light panels are a handful of draws, and a cut-away view can drop a storey or a ceiling whole
    const bands = new Map<string, THREE.Group>();
    const bandOf = (floor: number, ceiling: boolean): THREE.Group => {
      const k = `${floor}:${ceiling ? 'ceiling' : 'low'}`;
      let g = bands.get(k);
      if (!g) { g = new THREE.Group(); g.name = `fixtures:${k}`; g.userData.fixtureFloor = floor; g.userData.fixtureBand = ceiling ? 'ceiling' : 'low'; bands.set(k, g); root.add(g); }
      return g;
    };
    // A FIXTURE WITH A MODEL (C2 item 8 — slice N's `FIXTURE_MODEL`: the wall sconce is a generated piece now). The
    // procedural boxes are built first, in a band of their own, and stay until the model has loaded (the stand-in is
    // the self-heal: a missing asset, a slow link, the lever); then the clones take their place.
    const modelsOn = fixtureModelsEnabled();
    const standIns = new Map<string, THREE.Group>();
    const modelSeats: Array<{ key: string; fx: { x: number; y: number; z: number; yaw: number }; fy: number; floor: number; seat: { dy: number; dz: number } }> = [];
    let n = 0;
    for (const fx of list) {
      const ps = kitProp(fx.key);
      if (!ps) continue;
      const fy = fx.floor === 0 ? 0 : fx.floor * FLOOR_H; // slice F's convention (its bed's): a fixture's `y` is measured from the storey's nominal level
      const obj = this.propObject(session, ps);
      obj.position.set(fx.x, fy + fx.y, fx.z);
      obj.rotation.y = fx.yaw;
      const seat = modelsOn ? fixtureModelSeat(fx.key) : null;
      let band = bandOf(fx.floor, fx.y >= 2.3);
      if (seat) {
        let sg = standIns.get(seat.key);
        if (!sg) { sg = new THREE.Group(); sg.name = `fixtures:standin:${seat.key}`; sg.userData.fixtureStandIn = seat.key; standIns.set(seat.key, sg); root.add(sg); }
        band = sg;
        modelSeats.push({ key: seat.key, fx: { x: fx.x, y: fx.y, z: fx.z, yaw: fx.yaw }, fy, floor: fx.floor, seat });
      }
      band.add(obj);
      n++;
      if (fx.text) {
        // the number a plate carries: dynamic text, never baked into a mesh (slice D's plate texture), a hair proud of its face
        const tex = signTexture(fx.text, session.look, { kind: 'plate' });
        const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false });
        const face = new THREE.Mesh(new THREE.PlaneGeometry(ps.w * 0.86, ps.h * 0.8), mat);
        face.position.set(fx.x + Math.sin(fx.yaw) * (ps.d / 2 + 0.006), fy + fx.y + ps.h / 2, fx.z + Math.cos(fx.yaw) * (ps.d / 2 + 0.006));
        face.rotation.y = fx.yaw;
        face.name = 'interior-plate';
        face.userData.text = fx.text;
        band.add(face);
        plates.push(() => { disposeLater(tex); disposeLater(mat); });
      }
    }
    for (const g of bands.values()) mergeStaticMeshes(g, (m) => m.name === 'interior-plate' || !!m.userData.sharedAsset); // a cache-shared geometry is never merged
    for (const g of standIns.values()) mergeStaticMeshes(g);
    root.traverse((o) => { o.userData.interiorFixture = true; });
    root.userData.fixtureCount = n;
    root.userData.fixtureModels = 0;
    root.userData.release = plates;
    this.scene.add(root);
    session.fixtures = root;
    if (this.perf && this.perf.id === b.id) { this.perf.fixtureModels = 0; this.perf.fixtureStandIns = modelSeats.length; }
    if (modelSeats.length > 0) void this.seatFixtureModels(session, root, standIns, modelSeats, plates);
  }

  /** Swap a fixture's stand-in for its generated model once the master is in hand: a clone per seat, fitted and worn
   *  exactly as `placeSlot` seats a piece (`furnitureFit`, slice D's unlit adapter — which adds the emissive that
   *  makes a sconce read lit), at slice N's seat (`fixtureModelSeat`: its back on the wall, its middle at the
   *  fixture's middle). The clone's geometry is the asset cache's (`sharedAsset`: never merged, never disposed —
   *  the handle is released with the fixtures); its materials are ours. Any failure leaves the stand-in. */
  private async seatFixtureModels(
    session: NonNullable<BuildingInteriorModule['active']>, root: THREE.Group, standIns: Map<string, THREE.Group>,
    seats: Array<{ key: string; fx: { x: number; y: number; z: number; yaw: number }; fy: number; floor: number; seat: { dy: number; dz: number } }>, plates: Array<() => void>,
  ): Promise<void> {
    const conn = atlas.conn, assets = this.assets;
    if (!conn || !assets) return;
    for (const key of new Set(seats.map((s) => s.key))) {
      const fspec: FurnitureSpec | undefined = FURNITURE[key];
      let pm: { assetId: bigint } | undefined;
      try { pm = [...conn.db.prefabModel.iter()].find((p) => p.prefabKey === `prop:furn_${key}`); } catch { pm = undefined; }
      if (!fspec || !pm) continue; // no model registered: the stand-in stays
      try {
        const { object: master, release } = await assets.loadForTier(pm.assetId);
        if (this.active !== session || session.fixtures !== root) { release(); return; } // THE SESSION LAW: left, or the fixtures were rebuilt
        plates.push(release);
        const bb = new THREE.Box3().setFromObject(master);
        const { scale, yOff } = furnitureFit(fspec, bb);
        const worn = new Map<THREE.Material, THREE.Material>(); // one adapted material per source, shared by every clone
        const g = new THREE.Group();
        g.name = `fixtures:model:${key}`;
        g.userData.interiorFixture = true; g.userData.fixtureModel = key;
        let count = 0;
        for (const s of seats) {
          if (s.key !== key) continue;
          const clone = master.clone(true);
          clone.traverse((o) => {
            o.userData.sharedAsset = true; o.userData.interiorFixture = true;
            const mesh = o as THREE.Mesh;
            if ((mesh as { isMesh?: boolean }).isMesh && mesh.material) {
              const srcMat = mesh.material as THREE.MeshStandardMaterial;
              let m = worn.get(srcMat);
              if (!m) { const made = furnitureMaterial(srcMat, session.look); m = made; worn.set(srcMat, made); plates.push(() => disposeLater(made)); }
              mesh.material = m;
            }
          });
          const pose = fixtureModelPose(s.fx, s.fy, s.seat, yOff);
          clone.position.set(pose.x, pose.y, pose.z);
          clone.rotation.y = pose.yaw;
          clone.scale.setScalar(scale);
          clone.userData.fixtureFloor = s.floor; clone.userData.fixtureBand = 'low'; clone.userData.fixtureModel = key;
          g.add(clone);
          count++;
        }
        root.add(g);
        const sg = standIns.get(key);
        if (sg) { root.remove(sg); sg.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); }); }
        root.userData.fixtureModels = ((root.userData.fixtureModels as number | undefined) ?? 0) + count;
        if (this.perf && this.perf.id === session.b.id) { this.perf.fixtureModels += count; this.perf.fixtureStandIns = Math.max(0, this.perf.fixtureStandIns - count); }
      } catch { /* asset missing or failed: the stand-in stays */ }
    }
  }
  private dropFixtures(session: NonNullable<BuildingInteriorModule['active']>): void {
    const root = session.fixtures;
    if (!root) return;
    session.fixtures = null;
    this.scene.remove(root);
    // a fixture MODEL's geometry is the asset cache's — released by its handle below, never disposed (C2 item 8)
    root.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry && !o.userData.sharedAsset) disposeLater(m.geometry); });
    for (const rel of (root.userData.release as Array<() => void> | undefined) ?? []) rel();
  }

  /** Drop the seated pack furniture and seat the current pack (or nothing). */
  private async refurnishDeed(): Promise<void> {
    const session = this.active;
    if (!session) return;
    session.packGen++;
    for (const o of [...session.group.children]) if (o.userData.packFurniture) session.group.remove(o);
    const sink = session.packSink;
    session.packSink = [];
    for (const r of sink) { try { r(); } catch { /* released already */ } }
    // The pack's furniture colliders go with it (slice C); the shell's `int:` boxes are untouched.
    for (const cid of session.furnCids) this.physics.removeStaticCollider(cid);
    session.furnCids = [];
    this.dropItemColliders(session); // re-dealt against the new room's counts when it is seated
    const b = session.b;
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const toWorld = (lx: number, lz: number): { x: number; z: number } => ({ x: b.x + lx * cos + lz * sin, z: b.z - lx * sin + lz * cos });
    await this.furnishFor(session, toWorld, resolvePurpose(b.id, b.key).purpose); // the lever, the pin and the pack are re-read
  }

  /** The nameplate inside the door: the purpose a visitor reads on the way in
   *  (no sign for a purpose-less marker interior). Disposed with the group. */
  private placeSign(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number }, p: PurposeInfo,
  ): void {
    if (p.purpose === 'generic' || !this.active) return;
    // THE SIGN HANGS ON THE HEADER BAND (slice C, 2026-10-01): at 2.25 m it sat inside the window band and floated
    // on a pane in every room whose door-side wall has one near x 2.3. The lever keeps the old seat.
    const seat = signSeat(spec, !this.active.plannerOn);
    if (!seat) return; // no wall right of the door to hang it on
    const tex = signTexture(p.label, this.active.look);
    const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(seat.w, seat.h), mat);
    const wp = toWorld(seat.x, seat.z);
    mesh.position.set(wp.x, b.groundY + seat.y, wp.z);
    mesh.rotation.y = b.yaw; // faces +Z local — into the room
    mesh.name = 'interior-sign';
    group.add(mesh);
    this.active.releases.push(() => disposeLater(tex));
  }

  /** Load one furniture prefab and seat it in the active interior. Returns
   *  false when the player left mid-load (the caller stops furnishing). */
  private async placeSlot(
    session: NonNullable<BuildingInteriorModule['active']>, b: EnterableBuilding, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
    key: string, x: number, z: number, yaw: number, floorY: number, jitter = 0,
    opts: { sink?: Array<() => void>; tag?: string; floor?: number; index?: number; gen?: number; piece?: SeatPiece } = {},
  ): Promise<boolean> {
    const sink = opts.sink ?? session.releases;
    const conn = atlas.conn;
    const assets = this.assets;
    if (!conn || !assets) return false;
    const fspec: FurnitureSpec | undefined = FURNITURE[key];
    let tStage = performance.now();
    const assetId = this.furnAssetOf(key); // (slice H) one map per catalog change — the table used to be spread per piece
    this.perfCost('lookup', tStage);
    if (!fspec || assetId === undefined) return true; // model not generated yet — skip quietly (and no collider: nothing is drawn)
    try {
      tStage = performance.now();
      const { object: master, release } = await assets.loadForTier(assetId);
      this.perfCost('loadWait', tStage);
      // The player may have left (or re-entered) while loading.
      if (this.active !== session) { release(); return false; }
      // A newer pack took over while this piece loaded (2026-09-25): the refurnish already cleared the group, so
      // seating this one would leave a duplicate — the furniture probe listed 4 pieces of a 3-piece pack.
      if (opts.gen !== undefined && session.packGen !== opts.gen) { release(); return false; }
      sink.push(release);
      tStage = performance.now();
      const bb = new THREE.Box3().setFromObject(master);
      const { scale, yOff } = furnitureFit(fspec, bb);
      this.perfCost('fit', tStage); tStage = performance.now();
      const clone = master.clone(true);
      // Geometry is SHARED with the asset cache — exit() must release the
      // handle, never dispose it (the tag skips disposal). Materials are
      // REPLACED with unlit basics (the interior-materials law: interior
      // readability never depends on scene lighting — PBR goes black at
      // night); those are ours, disposed via the releases list.
      clone.traverse((o) => {
        o.userData.sharedAsset = true;
        const mesh = o as THREE.Mesh;
        if ((mesh as { isMesh?: boolean }).isMesh && mesh.material) {
          const src = mesh.material as THREE.MeshStandardMaterial;
          const basic = furnitureMaterial(src, session.look);
          mesh.material = basic;
          sink.push(() => disposeLater(basic)); // (bug sweep) exit() drains releases synchronously — a drawn material freed inside the submit window
        }
      });
      if (opts.tag) clone.userData[opts.tag] = true;
      // FUNCTIONAL FURNITURE (2026-09-24): every seated clone says what it is, for FurnitureUseModule.
      clone.userData.furnKey = key; clone.userData.furnFloor = opts.floor ?? 0; clone.userData.furnIndex = opts.index ?? 0;
      const wp = toWorld(x, z);
      clone.position.set(wp.x, b.groundY + floorY + yOff, wp.z);
      // THE LEFT DRAPE (slice H, 2026-10-02): the generated stage curtain is a RIGHT-hand drape; the stage kit stands the
      // left one half-turned (`drape0`, `turn: 2`), which showed the house its plain back. Drawn MIRRORED instead — the
      // half-turn undone, the model's own x negated — its swept front faces the house and its tie-back stays on the
      // outer side. three keys a mirrored mesh's front face on both backends (`determinantAffine() < 0`); the collider
      // keeps the planned (turned) pose — the same thin footprint. Lever: `atlas:interior-left-drape = 'off'`.
      const mirrored = !!opts.piece && key === 'stage_curtain' && /\/drape0#/.test(opts.piece.slot) && leftDrapeMirrored();
      clone.rotation.y = b.yaw + yaw + jitter + (mirrored ? Math.PI : 0);
      clone.scale.setScalar(scale);
      if (mirrored) { clone.scale.x = -scale; clone.userData.mirroredDrape = true; }
      group.add(clone);
      this.perfCost('clone', tStage); this.perfCount('clones'); tStage = performance.now();
      // A PLANNED piece says which slot it is, what key its storage has, and — when it blocks walking on a floor the
      // planner proved a capsule route around — gets its `int:` box from the model's OWN drawn bounds (slice C). The
      // box is added only now that the piece is drawn (a missing model must never leave an invisible wall) and is
      // removed with the visit (or with the pack). A legacy slot carries none of it and collides with nothing, as before.
      const sp = opts.piece;
      if (sp) {
        clone.userData.furnSlot = sp.slot; clone.userData.furnTier = sp.tier;
        if (sp.storageKey) clone.userData.furnStorageKey = sp.storageKey;
        clone.userData.furnStorageLive = sp.storageLive; // THE LIVE STORAGE (C2): false = plain furniture, no storage prompt
        if (sp.collide) {
          const cb = pieceColliderBox({ x, z, yaw, y: floorY }, { scale, yOff, min: [bb.min.x, bb.min.y, bb.min.z], max: [bb.max.x, bb.max.y, bb.max.z] });
          const cw = toWorld(cb.lx, cb.lz);
          const cid = `int:${b.id}:furn:${sp.floor}:${session.furnSeq++}`;
          if (this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [cb.hx, cb.hy, cb.hz] }, { x: cw.x, y: b.groundY + cb.ly, z: cw.z, yaw: b.yaw + cb.yaw, scale: 1 })) {
            session.furnCids.push(cid);
            clone.userData.furnCollider = cid;
            if (this.perf && this.perf.id === b.id && this.perf.ms.furnished === null) this.perf.furnitureColliders++;
          }
        }
      }
      this.perfCost('furnColliders', tStage);
      this.perfPiece(session, floorY); // entry-latency mark: first piece (measurement only)
    } catch { /* asset missing — leave the slot bare */ }
    return true;
  }

  /** A Merchant Hive premium store: warm retail — service counter, display
   *  shelving, holo displays, greenery at the door. Deterministic. */
  private async furnishHiveStore(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const hw = spec.hw, hd = spec.hd;
    const plan: Array<{ key: string; x: number; z: number; yaw: number }> = [
      { key: 'counter_bar', x: -hw * 0.18, z: hd - 1.15, yaw: Math.PI },
      { key: 'screen_holo', x: hw * 0.55, z: hd - 1.1, yaw: Math.PI },
      { key: 'shelf_unit', x: -hw + 1.0, z: -hd * 0.35, yaw: Math.PI / 2 },
      { key: 'shelf_unit', x: hw - 1.0, z: -hd * 0.35, yaw: -Math.PI / 2 },
      { key: 'lamp_floor', x: -hw + 1.0, z: -hd + 1.2, yaw: Math.PI / 4 },
      { key: 'lamp_floor', x: hw - 1.0, z: -hd + 1.2, yaw: -Math.PI / 4 },
      { key: 'plant_small', x: 2.0, z: -hd + 1.0, yaw: 0 },
      { key: 'rug_woven', x: 0, z: -hd * 0.15, yaw: 0 },
      { key: 'statue_small', x: 0, z: hd * 0.35, yaw: Math.PI },
    ];
    for (const slot of plan) {
      if (Math.abs(slot.x) > hw - 0.5 || Math.abs(slot.z) > hd - 0.5) continue;
      if (Math.abs(slot.x) < 1.4 && slot.z < -hd + 3.4) continue; // door corridor
      const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, 0.08);
      if (!alive) return;
    }
  }

  /** The guild hall's bespoke interior (2026-08-29): a columned GREAT HALL —
   *  long charter tables and benches down the nave toward the dais, a hearth
   *  and bar for the members — then a COUNCIL chamber (floor 1) and the
   *  guild ARCHIVE (floor 2). Deterministic; the structural columns, dais,
   *  and banners come from interiorSpec's guild-hall boxes. */
  private async furnishGuildHall(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const hw = spec.hw, hd = spec.hd, st = spec.stair;
    const clearOf = (x: number, z: number): boolean => {
      if (Math.abs(x) < 1.4 && z < -hd + 3.4) return false;               // door corridor
      if (x > st.x - 2.0 && x < st.x + 2.0 && z > st.z - st.d / 2 - 1.8 && z < st.z + st.d / 2 + 1.8) return false; // stairs
      return Math.abs(x) < hw - 0.55 && Math.abs(z) < hd - 0.55;
    };
    type Slot = { key: string; x: number; z: number; yaw: number };
    const plans: Slot[][] = [
      [ // floor 0 — the great hall: the charter line runs toward the dais
        { key: 'table_dining', x: 0, z: -hd * 0.28, yaw: Math.PI / 2 },
        { key: 'table_dining', x: 0, z: hd * 0.12, yaw: Math.PI / 2 },
        { key: 'bench_hall', x: 1.7, z: -hd * 0.28, yaw: -Math.PI / 2 },
        { key: 'bench_hall', x: -1.7, z: -hd * 0.28, yaw: Math.PI / 2 },
        { key: 'bench_hall', x: 1.7, z: hd * 0.12, yaw: -Math.PI / 2 },
        { key: 'bench_hall', x: -1.7, z: hd * 0.12, yaw: Math.PI / 2 },
        { key: 'rug_woven', x: 0, z: -hd * 0.08, yaw: Math.PI / 2 },
        { key: 'stove_hearth', x: hw - 1.3, z: hd - 3.3, yaw: -Math.PI / 2 },
        { key: 'counter_bar', x: -hw * 0.5, z: hd - 1.15, yaw: Math.PI },
        { key: 'light_column', x: hw * 0.6, z: -hd + 4.2, yaw: 0 },
        { key: 'light_column', x: -hw * 0.6, z: hd - 4.6, yaw: 0 },
        { key: 'statue_small', x: 2.3, z: -hd + 1.2, yaw: 0 },
        { key: 'statue_small', x: -2.3, z: -hd + 1.2, yaw: 0 },
        { key: 'plant_large', x: hw - 1.1, z: -hd + 1.5, yaw: 0 },
        { key: 'screen_holo', x: 2.6, z: hd - 3.4, yaw: Math.PI },
      ],
      [ // floor 1 — the council chamber: a ring around one table
        { key: 'table_dining', x: 0.6, z: 0.4, yaw: 0 },
        { key: 'chair_dining', x: 0.6, z: -1.1, yaw: 0 },
        { key: 'chair_dining', x: 0.6, z: 1.9, yaw: Math.PI },
        { key: 'chair_dining', x: 2.2, z: 0.4, yaw: -Math.PI / 2 },
        { key: 'chair_dining', x: -1.0, z: 0.4, yaw: Math.PI / 2 },
        { key: 'rug_woven', x: 0.6, z: 0.4, yaw: 0 },
        { key: 'bookcase_arch', x: hw - 0.95, z: 1.6, yaw: -Math.PI / 2 },
        { key: 'screen_holo', x: 0.6, z: hd - 1.1, yaw: Math.PI },
        { key: 'lamp_floor', x: -2.6, z: hd - 1.2, yaw: Math.PI * 0.75 },
        { key: 'plant_small', x: hw - 0.9, z: -hd + 1.2, yaw: 0 },
      ],
      [ // floor 2 — the guild archive: records, terminals, the quiet work
        { key: 'bookcase_arch', x: hw - 0.95, z: 2.2, yaw: -Math.PI / 2 },
        { key: 'bookcase_arch', x: hw - 0.95, z: -1.4, yaw: -Math.PI / 2 },
        { key: 'desk_terminal', x: 1.8, z: hd - 1.05, yaw: Math.PI },
        { key: 'chair_dining', x: 1.8, z: hd - 2.15, yaw: 0 },
        { key: 'desk_writing', x: -3.0, z: hd - 1.05, yaw: Math.PI },
        { key: 'chair_dining', x: -3.0, z: hd - 2.15, yaw: 0 },
        { key: 'cabinet_low', x: 2.6, z: -hd + 1.1, yaw: 0 },
        { key: 'terrarium_pod', x: hw - 1.0, z: -hd + 1.4, yaw: 0 },
        { key: 'lamp_floor', x: -2.4, z: -hd + 1.2, yaw: Math.PI / 4 },
      ],
    ];
    const floors = Math.min(spec.floors, plans.length);
    for (let f = 0; f < floors; f++) {
      for (const slot of plans[f]) {
        if (!clearOf(slot.x, slot.z)) continue;
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * 3.4);
        if (!alive) return;
      }
    }
  }

  // ─── C2: THE INDOOR RESCUE, CRAFTED PIECES' COLLIDERS ──────────────────────────────────────────────────────────────

  /**
   * THE INDOOR RESCUE (C2 item 2), every frame of a visit. Indoors the terrain clamp, the unstick watchdogs and the
   * terrain-seated corrections all stand down, so a capsule under the floor is nobody's to bring back: more than a
   * storey under the ground floor — or wedged under the slab inside the room for most of a second — it is set back on
   * the ground floor at the nearest LEGAL spot to where it is (`clampIndoor`: the smallest move, so the server row
   * stays within a soft correction). Counted, logged, never silent. The lever `atlas:interior-safety = 'off'` parks it.
   */
  private rescueTick(): void {
    const a = this.active;
    if (!a || a.safety === 'off' || !this.physics?.ready) return;
    const b = a.b;
    const dy = atlas.localY - b.groundY;
    if (dy >= -RESCUE_UNDER_M) { this.underSince = 0; return; }
    const now = performance.now();
    if (now - this.rescueAt < 1500) return; // one at a time: the teleport lands over the next fixed steps
    if (this.underSince === 0) this.underSince = now;
    const c = Math.cos(b.yaw), sn = Math.sin(b.yaw);
    const dx = atlas.localX - b.x, dz = atlas.localZ - b.z;
    const lx = c * dx - sn * dz, lz = sn * dx + c * dz;
    const inFootprint = Math.abs(lx) <= a.spec.hw + SAFETY_REACH_M && Math.abs(lz) <= a.spec.hd + SAFETY_REACH_M;
    const why = rescueVerdict(dy, inFootprint, now - this.underSince);
    if (!why) return;
    const p = clampIndoor(b, atlas.localX, atlas.localZ);
    const y = b.groundY + 0.08 + 0.06;
    this.physics.teleportAvatarY(p.x, y, p.z);
    this.rescues++;
    this.rescueAt = now;
    this.underSince = 0;
    this.lastRescue = { id: b.id, why, dy: Math.round(dy * 100) / 100, at: Math.round(now) };
    console.warn(`[BuildingInterior] indoor rescue #${this.rescues} in ${b.id}: the capsule was ${(-dy).toFixed(2)} m under the ground floor (${why}) — set back on the floor at local (${(c * (p.x - b.x) - sn * (p.z - b.z)).toFixed(1)}, ${(sn * (p.x - b.x) + c * (p.z - b.z)).toFixed(1)})`);
  }

  /** A crafted piece's fitted bounds: the decor model `DecorationModule` draws, measured once per key. */
  private itemFit(key: string): ItemFit | null | Promise<ItemFit | null> {
    const hit = this.itemFits.get(key);
    if (hit !== undefined) return hit;
    const conn = atlas.conn, assets = this.assets, fspec: FurnitureSpec | undefined = FURNITURE[key];
    if (!conn || !assets || !fspec) return null;
    let assetId: bigint | null = null;
    try { for (const p of conn.db.prefabModel.iter()) { if (p.prefabKey === `decor:furn_${key}`) { assetId = p.assetId; break; } if (p.prefabKey === `prop:furn_${key}` && assetId === null) assetId = p.assetId; } } catch { return null; }
    if (assetId === null) return null; // no model registered yet: nothing is drawn, so nothing is solid (retried next visit)
    const job = assets.loadForTier(assetId).then(({ object, release }) => {
      const bb = new THREE.Box3().setFromObject(object);
      const { scale, yOff } = furnitureFit(fspec, bb);
      release(); // the box is all this needs; DecorationModule holds the drawn copy
      const fit: ItemFit = { scale, yOff, min: [bb.min.x, bb.min.y, bb.min.z], max: [bb.max.x, bb.max.y, bb.max.z] };
      this.itemFits.set(key, fit);
      return fit;
    }, () => { this.itemFits.delete(key); return null; });
    this.itemFits.set(key, job);
    return job;
  }

  /**
   * CRAFTED PIECES COLLIDE TOO (C2 item 3). A `structure_item` the owner placed in this deed that blocks walking by the
   * kit table gets the box a planned piece gets (`int:<id>:item:<itemId>`, from the model's own fitted bounds, in the
   * STRUCTURE's frame as `DecorationModule` draws it), inside the same 40-a-floor cap. Re-dealt at ~2 Hz: a piece
   * placed, moved or picked up mid-visit gains, moves or loses its box; all leave with the visit. Under the lever
   * (`atlas:interior-planner = 'off'`) nothing here runs — today's room has no furniture colliders at all.
   */
  private async syncItemColliders(session: NonNullable<BuildingInteriorModule['active']>): Promise<void> {
    const { b } = session;
    if (this.active !== session || !session.plannerOn || !b.id.startsWith('deed:') || session.plan.route === 'pending') return;
    const conn = atlas.conn;
    if (!conn) return;
    let sid: bigint;
    try { sid = BigInt(b.id.slice(5)); } catch { return; }
    type ItemRow = { itemId: bigint; structureId: bigint; prefabId: string; lx: number; ly: number; lz: number; pitch: number; yaw: number; roll: number };
    const db = conn.db as unknown as { structureItem?: { iter(): Iterable<ItemRow> }; structure: { structureId: { find(id: bigint): { posX: number; posZ: number; yaw: number } | undefined } } };
    const rows: ItemRow[] = [];
    let s: { posX: number; posZ: number; yaw: number } | undefined;
    try { s = db.structure.structureId.find(sid); if (db.structureItem) for (const it of db.structureItem.iter()) if (it.structureId === sid) rows.push(it); } catch { return; }
    if (!s) { this.dropItemColliders(session); return; }
    const planned = new Map<number, number>();
    for (const cid of session.furnCids) { const m = /:furn:(\d+):/.exec(cid); if (m) planned.set(Number(m[1]), (planned.get(Number(m[1])) ?? 0) + 1); }
    const picks = itemColliderPicks(rows.map((r) => ({ id: r.itemId, prefabId: r.prefabId, ly: r.ly })), session.spec.floors, (f) => planned.get(f) ?? 0);
    const want = new Map(picks.map((p) => [p.id, p]));
    for (const [id, v] of [...session.itemCids]) if (!want.has(id)) { this.physics.removeStaticCollider(v.cid); session.itemCids.delete(id); }
    const seatY = structureSeats.get(sid.toString())?.y ?? b.groundY;
    const cs = Math.cos(s.yaw), sn = Math.sin(s.yaw);
    for (const pick of picks) {
      const row = rows.find((r) => r.itemId.toString() === pick.id)!;
      const sig = `${row.lx}|${row.ly}|${row.lz}|${row.pitch}|${row.yaw}|${row.roll}|${seatY}|${s.posX}|${s.posZ}|${s.yaw}`;
      const have = session.itemCids.get(pick.id);
      if (have && have.sig === sig) continue;
      const fitOrJob = this.itemFit(pick.key);
      const fit = fitOrJob instanceof Promise ? await fitOrJob : fitOrJob;
      if (this.active !== session) return; // THE SESSION LAW
      if (!fit) continue; // no model: nothing drawn, nothing solid
      const cur = session.itemCids.get(pick.id);
      if (cur) { if (cur.sig === sig) continue; this.physics.removeStaticCollider(cur.cid); session.itemCids.delete(pick.id); }
      const cb = itemColliderBox(row, fit);
      const cid = `int:${b.id}:item:${pick.id}`;
      if (this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [cb.hx, cb.hy, cb.hz] },
        { x: s.posX + cb.lx * cs + cb.lz * sn, y: seatY + cb.ly, z: s.posZ - cb.lx * sn + cb.lz * cs, yaw: s.yaw + cb.yaw, scale: 1 })) {
        session.itemCids.set(pick.id, { cid, sig, floor: pick.floor });
      }
    }
  }

  private dropItemColliders(session: NonNullable<BuildingInteriorModule['active']>): void {
    for (const v of session.itemCids.values()) this.physics.removeStaticCollider(v.cid);
    session.itemCids.clear();
  }

  private exit(skipRestore = false): void {
    const a = this.active;
    if (!a) return;
    this.scene.remove(a.group);
    // Deferred (warp-teardown law): pressing E to leave is a mid-frame teardown
    // of geometry the in-flight submit may still reference.
    a.group.traverse((o) => {
      if (o.userData.sharedAsset) return; // asset-cache geometry — release only
      const m = o as THREE.Mesh;
      if (m.geometry) disposeLater(m.geometry);
    });
    // Shared per-entry materials — dispose each once (never the asset cache's).
    const seen = new Set<THREE.Material>();
    a.group.traverse((o) => {
      if (o.userData.sharedAsset) return;
      const m = (o as THREE.Mesh).material as THREE.Material | undefined;
      if (m && !seen.has(m)) { seen.add(m); disposeLater(m); }
    });
    // a prop's material that no mesh of the group wears any more (its pack was re-seated away) is still the visit's to free
    for (const m of a.propMats) if (!seen.has(m)) { seen.add(m); disposeLater(m); }
    this.dropFixtures(a);
    this.dropPartitions(a);   // (slice G) the rooms' walls and their boxes leave with the visit
    this.presenceLeave();     // … and the server is told
    for (const rel of a.releases) rel();
    for (const rel of a.packSink) rel(); // the pack furniture's own releases (re-seated in place on a pack change)
    for (const cid of a.cids) this.physics.removeStaticCollider(cid);
    for (const cid of a.furnCids) this.physics.removeStaticCollider(cid); // the furniture's boxes leave with the visit (slice C)
    this.dropItemColliders(a);                                             // … and the crafted pieces' (C2)
    if (a.safetyCid) this.physics.removeStaticCollider(a.safetyCid);       // … and the last-resort ground
    if (!skipRestore) a.b.restore();
    setIndoorBodyLight(null); // (slice H) no room, no room light
    this.active = null;
    atlas.interiorId = null;
    setActiveInterior(null);
    if (this.debugHandle) { this.debugHandle.dispose(); this.debugHandle = null; this.debugVersion = -1; }
    this.nearDoorId = null;
  }

  dispose(): void {
    this.exit();
    if (this.prewarm?.settle) this.prewarm.settle();
    this.prewarm = null;
    for (const o of this.prewarmOld) o.settle();
    this.prewarmOld.length = 0;
    if (this.onKey) window.removeEventListener('keydown', this.onKey);
    this.onKey = null;
    this.prompt?.remove();
    for (const [, gM] of this.doorMarkers) {
      this.scene.remove(gM);
      gM.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    }
    this.doorMarkers.clear();
    this.markerMats?.jamb.dispose();
    this.markerMats?.portal.dispose();
  }
}
